import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, XCircle, Info, Moon, Sun } from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  ComposedChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  Radar as RechartsRadar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from "recharts";
import Constants from "../../../../Constants.json";

const C = {
  green: "#6f8a5b",
  green2: "#8da57a",
  olive: "#a9b38a",
  amber: "#caa85e",
  orange: "#d28a52",
  red: "#c6523f",
};

const THEME_STORAGE_KEY = "tickerTheme";

function applyTheme(dark) {
  if (typeof document === "undefined") return;
  const r = document.documentElement;
  if (dark) {
    r.style.setProperty("--app-bg-from", "#0c1018");
    r.style.setProperty("--app-bg-to", "#121a26");
    r.style.setProperty("--card-bg", "rgba(18,26,38,0.92)");
    r.style.setProperty("--card-border", "rgba(148,163,184,.14)");
    r.style.setProperty("--text-strong", "#e8edf4");
    r.style.setProperty("--text", "#cbd5e1");
    r.style.setProperty("--muted", "#94a3b8");
    r.style.setProperty("--badge-bg", "rgba(111,138,91,.22)");
    r.style.setProperty("--badge-fg", "#c4d4b8");
    r.style.setProperty("--sep", "rgba(148,163,184,0.12)");
    r.style.setProperty("--summary-bg", "rgba(15,23,42,0.55)");
    r.style.setProperty("--grid-line", "rgba(148,163,184,0.10)");
    r.style.setProperty("--hero-desc-border", "rgba(167, 139, 250, 0.45)");
    r.style.setProperty("--hero-desc-fg", "#c4d4b8");
    r.style.setProperty("--profile-info-muted", "rgba(148,163,184,0.55)");
    r.style.setProperty("--gauge-needle", "#e8edf4");
    r.style.setProperty("--cta-bg", "rgba(255,255,255,0.10)");
    r.style.setProperty("--cta-fg", "#e8edf4");
    r.style.setProperty("--cta-border", "rgba(148,163,184,0.22)");
  } else {
    r.style.setProperty("--app-bg-from", "#f6f5f1");
    r.style.setProperty("--app-bg-to", "#f6f5f1");
    r.style.setProperty("--card-bg", "rgba(255,255,255,0.86)");
    r.style.setProperty("--card-border", "rgba(30,40,34,.10)");
    r.style.setProperty("--text-strong", "#1f2a24");
    r.style.setProperty("--text", "#243028");
    r.style.setProperty("--muted", "#6f7b74");
    r.style.setProperty("--badge-bg", "rgba(181,154,91,.18)");
    r.style.setProperty("--badge-fg", "#4a3d22");
    r.style.setProperty("--sep", "rgba(30,40,34,0.08)");
    r.style.setProperty("--summary-bg", "rgba(255,255,255,0.50)");
    r.style.setProperty("--grid-line", "rgba(30,40,34,0.07)");
    r.style.setProperty("--hero-desc-border", "rgba(139, 92, 246, 0.42)");
    r.style.setProperty("--hero-desc-fg", "#5a6b4f");
    r.style.setProperty("--profile-info-muted", "rgba(111,123,116,0.65)");
    r.style.setProperty("--gauge-needle", "#1f2a24");
    r.style.setProperty("--cta-bg", "#eef0f2");
    r.style.setProperty("--cta-fg", "#243046");
    r.style.setProperty("--cta-border", "rgba(30,40,34,0.12)");
  }
}

function getStoredDarkMode() {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(THEME_STORAGE_KEY) === "dark";
}

/** Prix formaté dans la devise locale quand disponible. */
function fmtHeroPrice(price, exchangeOrCurrency, maxDecimals = 2) {
  if (price == null || !Number.isFinite(Number(price))) return "—";
  const raw = String(exchangeOrCurrency || "").toUpperCase().trim();
  const looksLikeCcy = /^[A-Z]{3}$/.test(raw);
  const ccy = looksLikeCcy ? raw : null;
  const ex = looksLikeCcy ? "" : raw;
  const us = ["NYSE", "NASDAQ", "AMEX", "BATS", "OTC", "ARCA"].some((x) => ex.includes(x)) || /\bUS\b/.test(ex);
  const n = fmtNum(Number(price), maxDecimals);
  if (ccy) return `${n} ${ccy}`;
  return us ? `$${n}` : `${n} €`;
}

function radarQualLabel(score01to100, language) {
  const v = Number(score01to100);
  if (!Number.isFinite(v)) return "";
  if (language === "en") {
    if (v >= 70) return "High";
    if (v >= 45) return "Average";
    return "Weak";
  }
  if (v >= 70) return "Élevé";
  if (v >= 45) return "Moyen";
  return "Faible";
}

function formatVolumeAxisTick(v) {
  if (v == null || !Number.isFinite(Number(v))) return "";
  const x = Number(v);
  if (x === 0) return "0";
  if (Math.abs(x) >= 1e9) return `${(x / 1e9).toFixed(1)}B`;
  if (Math.abs(x) >= 1e6) return `${(x / 1e6).toFixed(1)}M`;
  if (Math.abs(x) >= 1e3) return `${(x / 1e3).toFixed(0)}k`;
  return String(Math.round(x));
}

function ProfileRow({ label, value, hint }) {
  const show = value != null && String(value).trim() !== "";
  return (
    <div
      className="profile-row"
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        padding: "7px 0",
        borderBottom: "1px solid var(--sep)",
        alignItems: "center",
      }}
    >
      <span
        className="profile-row-label"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          color: "var(--muted)",
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: "0.01em",
          flexShrink: 0,
        }}
      >
        {label}
        {hint ? <TipMark title={hint} /> : null}
      </span>
      <span style={{ color: "var(--text-strong)", fontWeight: 700, fontSize: 13, textAlign: "right", wordBreak: "break-word" }}>{show ? String(value).trim() : "—"}</span>
    </div>
  );
}

function HeroCardHead({ title, companyName, kicker, priceStr, mtdPct, ytdPct, badge, darkMode, onToggleDark, t }) {
  const mtdOk = mtdPct != null && Number.isFinite(Number(mtdPct));
  const ytdOk = ytdPct != null && Number.isFinite(Number(ytdPct));
  const mtdCol = mtdOk && Number(mtdPct) >= 0 ? C.green : C.red;
  const ytdCol = ytdOk && Number(ytdPct) >= 0 ? C.green : C.red;
  return (
    <div className="hero-card-head">
      <button
        type="button"
        className="theme-fab"
        onClick={onToggleDark}
        aria-pressed={darkMode}
        aria-label={darkMode ? t("dayMode") : t("nightMode")}
      >
        {darkMode ? <Sun size={18} strokeWidth={2} /> : <Moon size={18} strokeWidth={2} />}
      </button>
      <div className="hero-card-head-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0, flex: "1 1 200px" }}>
          <div style={{ color: "var(--text-strong)", fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.15 }}>{title}</div>
          {companyName && String(companyName).trim() && String(companyName).trim() !== String(title).trim() ? (
            <div style={{ marginTop: 5, fontSize: 14, color: "var(--muted)", fontWeight: 600, lineHeight: 1.25 }}>{companyName}</div>
          ) : null}
          {kicker ? (
            <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px 12px" }}>
              <span style={{ color: "#b79a51", fontSize: 14, fontWeight: 600 }}>{kicker}</span>
              {mtdOk ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: mtdCol, fontWeight: 700, fontSize: 13, fontFamily: "ui-monospace,monospace" }}>
                  {fmtPct(mtdPct)} {t("mtdShort")}
                  <span className="profile-info-ic" title={t("mtdHint")} style={{ display: "inline-flex", color: "var(--profile-info-muted)" }}>
                    <Info size={12} strokeWidth={2} aria-hidden />
                  </span>
                </span>
              ) : null}
              {ytdOk ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: ytdCol, fontWeight: 700, fontSize: 13, fontFamily: "ui-monospace,monospace" }}>
                  {fmtPct(ytdPct)} {t("ytdShort")}
                  <span className="profile-info-ic" title={t("ytdHint")} style={{ display: "inline-flex", color: "var(--profile-info-muted)" }}>
                    <Info size={12} strokeWidth={2} aria-hidden />
                  </span>
                </span>
              ) : null}
            </div>
          ) : (
            <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: "8px 14px" }}>
              {mtdOk ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: mtdCol, fontWeight: 700, fontSize: 13, fontFamily: "ui-monospace,monospace" }}>
                  {fmtPct(mtdPct)} {t("mtdShort")}
                  <span className="profile-info-ic" title={t("mtdHint")} style={{ display: "inline-flex", color: "var(--profile-info-muted)" }}>
                    <Info size={12} strokeWidth={2} aria-hidden />
                  </span>
                </span>
              ) : null}
              {ytdOk ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: ytdCol, fontWeight: 700, fontSize: 13, fontFamily: "ui-monospace,monospace" }}>
                  {fmtPct(ytdPct)} {t("ytdShort")}
                  <span className="profile-info-ic" title={t("ytdHint")} style={{ display: "inline-flex", color: "var(--profile-info-muted)" }}>
                    <Info size={12} strokeWidth={2} aria-hidden />
                  </span>
                </span>
              ) : null}
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 }}>
          {badge}
          <div style={{ color: "var(--text-strong)", fontSize: 26, fontWeight: 800, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", lineHeight: 1, letterSpacing: "-0.02em" }}>{priceStr}</div>
        </div>
      </div>
      <Sep />
    </div>
  );
}

const I18N = {
  fr: {
    back: "← Retour au Flux de capitaux",
    loading: "Chargement...",
    decision: "Facteurs de décision",
    fundamentals: "Fondamentaux",
    valuation: "Valorisation",
    marketStructure: "Structure du Marché",
    timing: "Timing de marché",
    risk: "Risque & Rendement",
    financial: "État Financier",
    distribution: "Distribution",
    dividendsCalendar: "Calendrier des dividendes",
    period: "Période",
    detailed: "Analyse détaillée",
    detailedHint: "Sélectionne une carte avec \"Voir analyse\" pour afficher le détail ici.",
    noData: "Données indisponibles sur cette section.",
    calcSource: "Source de calcul",
    seeAnalysis: "Voir analyse",
    close: "Fermer",
    favorable: "Favorable",
    vigilance: "Vigilance",
    unfavorable: "Défavorable",
    seeMore: "Voir plus ↓",
    seeLess: "Réduire ↑",
    descriptionSection: "DESCRIPTION",
    asOf: "Données au",
    inReview: "En suivi",
    efCoverageSubtitle: "Couverture du Cash Flow",
    efCovered: "Couvert",
    efInsufficient: "Insuffisant",
    efUncovered: "Non couvert",
    efNA: "N/A",
    efAutonomous: "Autonome",
    efPartial: "Partiel",
    efExternalLeverage: "Levier externe",
    efBfr: "Δ BFR",
    efCapex: "CAPEX",
    efDebt: "Dette",
    efShareholders: "Actionnaires",
    efCfoTip: "Cash Flow Opérationnel (CFO) : trésorerie générée par l'activité avant investissements et financement.",
    efNeedsTip: "Total des besoins à couvrir : BFR, CAPEX, service de la dette, rémunération des actionnaires.",
    efMeterLeft: "← Couvert",
    efMeterRight: "Non couvert →",
    efCfoPointer: "CFO",
    distLegendPaid: "Mois payé",
    distLegendNone: "Aucun dividende",
    distTipScore:
      "Score qualité dividende (0–100) : yield, payout, croissance 5 ans, couverture FCF, régularité.",
    distTipYield: "Rendement dividende = dividende annuel / cours.",
    distTipFreq: "Rythme de versement (données profil).",
    distTipPayout: "Dividende / résultat : >100 % ou >1× peut signaler un versement au-delà du résultat.",
    distTipFcf: "Free cash flow / dividendes versés. ≥1,5× confortable ; <1× risque de couverture.",
    distTipGrowth: "CAGR du dividende sur 5 ans.",
    distTipRegularity: "Années consécutives de versement lorsque disponible en base.",
    distMonthsPaid: "mois / 12",
    distPerYear: "EUR / an",
    nightMode: "Mode nuit",
    dayMode: "Mode jour",
    profileSection: "PROFIL",
    profRegion: "Région",
    profExchange: "Bourse",
    profSector: "Secteur",
    profIndustry: "Industrie",
    profCap: "Capitalisation",
    profStyle: "Style",
    profCycle: "Cycle",
    profRegionHint: "Zone géographique et marché principal de l'émetteur.",
    profExchangeHint: "Marché réglementé sur lequel le titre est coté.",
    profSectorHint: "Secteur économique (classification large).",
    profIndustryHint: "Industrie ou sous-secteur détaillé.",
    profCapHint: "Segment de capitalisation (Large, Mid, Small…).",
    profStyleHint: "Style d'investissement (Core, Value, Growth…).",
    profCycleHint: "Sensibilité au cycle économique (cyclique, défensif…).",
    mtdShort: "MTD",
    ytdShort: "YTD",
    mtdHint: "Variation du cours depuis le début du mois civil.",
    ytdHint: "Variation du cours depuis le début de l'année civile.",
    iscoreHint: "Score de conviction interne (0–100) synthétisant les piliers d'analyse.",
    iscoreLabel: "ISCORE",
    valuationHistNA: "Hist. n/d",
    gaugeAxisFair: "Juste valeur",
    gaugeCornerLow: "Faible",
    gaugeCornerHigh: "Élevée",
    msIhhTip: "Indice Herfindahl-Hirschman (IHH) de concentration sectorielle: 0 = très fragmenté, 10 000 = quasi-monopole.",
    msTop3Tip: "Part de marché cumulée des 3 plus grands acteurs du secteur.",
    msHhiTrendTip: "Variation de concentration IHH sur 1 an (en points).",
    msRoicTip: "ROIC sectoriel moyen et évolution sur 3 ans (delta en points).",
    msWinnersTip: "Part des entreprises du secteur qui gagnent des parts de marché.",
    msPhaseTip: "Phase concurrentielle synthétique du secteur.",
    msClusterTip: "Cluster stratégique détecté pour la structure de marché.",
    timingMma20Tip: "Moyenne mobile sur 20 jours : suit la tendance à court terme.",
    timingMma50Tip: "Moyenne mobile sur 50 jours : confirme la tendance intermédiaire.",
    timingMma200Tip: "Moyenne mobile sur 200 jours : repère la tendance de fond.",
    timingTrendTip: "Direction récente du prix sur la période affichée.",
    timingVolumeTip: "Lecture des volumes d'échange : intensité des flux acheteurs/vendeurs.",
    timingMomentumTip: "Dynamique court terme du prix (accélération / ralentissement).",
    timingStructureTip: "Position du prix par rapport aux moyennes longues (notamment MMA 200).",
    btTipInitial: "Capital de départ hypothétique investi au début de la période de backtest.",
    btTipDividends: "Dividendes cumulés encaissés sur la période.",
    btTipGain: "Gain/perte absolu(e) entre le montant final et le capital initial.",
    btTipFinal: "Montant final total return (prix + dividendes réinvestis).",
    backToMarketFlow: "← Retour au Flux de Marché",
    backToMarketLeaders: "← Retour aux Leaders de Marché",
  },
  en: {
    back: "← Back to Market Flow",
    loading: "Loading...",
    decision: "Decision factors",
    fundamentals: "Fundamentals",
    valuation: "Valuation",
    marketStructure: "Market Structure",
    timing: "Market timing",
    risk: "Risk & Return",
    financial: "Financial Health",
    distribution: "Distribution",
    dividendsCalendar: "Dividend calendar",
    period: "Period",
    detailed: "Detailed analysis",
    detailedHint: "Select a card with \"See analysis\" to show details here.",
    noData: "No data available for this section.",
    calcSource: "Calculation source",
    seeAnalysis: "See analysis",
    close: "Close",
    favorable: "Favorable",
    vigilance: "Watch",
    unfavorable: "Unfavorable",
    seeMore: "See more ↓",
    seeLess: "Show less ↑",
    descriptionSection: "DESCRIPTION",
    asOf: "As of",
    inReview: "Under review",
    efCoverageSubtitle: "Cash flow coverage",
    efCovered: "Covered",
    efInsufficient: "Insufficient",
    efUncovered: "Not covered",
    efNA: "N/A",
    efAutonomous: "Self-funded",
    efPartial: "Partial",
    efExternalLeverage: "External leverage",
    efBfr: "Δ NWC",
    efCapex: "CAPEX",
    efDebt: "Debt",
    efShareholders: "Shareholders",
    efCfoTip: "Operating cash flow (CFO): cash from operations before investing and financing.",
    efNeedsTip: "Total needs: working capital, capex, debt service, shareholder payouts.",
    efMeterLeft: "← Covered",
    efMeterRight: "Not covered →",
    efCfoPointer: "CFO",
    distLegendPaid: "Paid month",
    distLegendNone: "No dividend",
    distTipScore: "Dividend quality score (0–100): yield, payout, 5Y growth, FCF cover, regularity.",
    distTipYield: "Dividend yield = annual dividend / price.",
    distTipFreq: "Payment cadence (profile data).",
    distTipPayout: "Dividend / earnings: >100% or >1× may indicate payout above earnings.",
    distTipFcf: "Free cash flow / dividends paid. ≥1.5× comfortable; <1× weak cover.",
    distTipGrowth: "5-year dividend CAGR.",
    distTipRegularity: "Consecutive payment years when available.",
    distMonthsPaid: "months / 12",
    distPerYear: "EUR / year",
    nightMode: "Night mode",
    dayMode: "Day mode",
    profileSection: "PROFILE",
    profRegion: "Region",
    profExchange: "Exchange",
    profSector: "Sector",
    profIndustry: "Industry",
    profCap: "Market cap",
    profStyle: "Style",
    profCycle: "Cycle",
    profRegionHint: "Issuer geography and primary listing region.",
    profExchangeHint: "Regulated market where the security is listed.",
    profSectorHint: "Economic sector (broad classification).",
    profIndustryHint: "Industry or detailed sub-sector.",
    profCapHint: "Market cap segment (large, mid, small…).",
    profStyleHint: "Investment style (core, value, growth…).",
    profCycleHint: "Economic cycle sensitivity (cyclical, defensive…).",
    mtdShort: "MTD",
    ytdShort: "YTD",
    mtdHint: "Price change month-to-date.",
    ytdHint: "Price change year-to-date.",
    iscoreHint: "Internal conviction score (0–100) aggregating analysis pillars.",
    iscoreLabel: "ISCORE",
    valuationHistNA: "Hist. n/a",
    gaugeAxisFair: "Fair value",
    gaugeCornerLow: "Low",
    gaugeCornerHigh: "High",
    msIhhTip: "Herfindahl-Hirschman Index (HHI) of market concentration: 0 = highly fragmented, 10,000 = near monopoly.",
    msTop3Tip: "Combined market share of the top 3 players in the sector.",
    msHhiTrendTip: "1-year change in HHI concentration (in points).",
    msRoicTip: "Sector average ROIC and its 3-year evolution (delta in points).",
    msWinnersTip: "Share of sector companies gaining market share.",
    msPhaseTip: "Synthetic competitive phase for the sector.",
    msClusterTip: "Detected strategic cluster for market structure.",
    timingMma20Tip: "20-day moving average: tracks short-term trend.",
    timingMma50Tip: "50-day moving average: confirms medium-term trend.",
    timingMma200Tip: "200-day moving average: identifies long-term trend.",
    timingTrendTip: "Recent price direction over the displayed period.",
    timingVolumeTip: "Trading volume reading: buying/selling flow intensity.",
    timingMomentumTip: "Short-term price dynamics (acceleration/deceleration).",
    timingStructureTip: "Price position versus long moving averages (especially MA 200).",
    btTipInitial: "Hypothetical starting capital invested at the beginning of the backtest period.",
    btTipDividends: "Cumulative dividends received over the period.",
    btTipGain: "Absolute gain/loss between final amount and initial capital.",
    btTipFinal: "Final total-return amount (price + reinvested dividends).",
    backToMarketFlow: "← Back to Market Flow",
    backToMarketLeaders: "← Back to Market Leaders",
  },
};

function getLanguage() {
  if (typeof window === "undefined") return "fr";
  const saved = localStorage.getItem("language");
  return saved === "en" ? "en" : "fr";
}

function fmtNum(n, max = 2) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const v = Number(n);
  const abs = Math.abs(v);
  let fd = max;
  if (abs > 0 && abs < 1) fd = 4;
  if (abs > 0 && abs < 0.01) fd = 6;
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: fd }).format(v);
}
function fmtPct(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const raw = Number(n);
  const v = Math.abs(raw) <= 1 ? raw * 100 : raw;
  return `${v > 0 ? "+" : ""}${v.toFixed(1)}%`;
}
function fmtPts(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const v = Number(n);
  const abs = Math.abs(v);
  if (abs > 0 && abs < 0.1) return `${v > 0 ? "+" : "−"}<0.1 pts`;
  return `${v > 0 ? "+" : ""}${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: abs < 1 ? 2 : 1 }).format(v)} pts`;
}
function toneDelta(n) {
  if (n == null || !Number.isFinite(Number(n))) return "var(--text-strong)";
  const v = Number(n);
  if (v > 0.1) return C.green;
  if (v < -0.1) return C.red;
  return C.amber;
}
function toneFromTag(tag, fallback = "neutral") {
  const s = String(tag || "").toLowerCase();
  if (s.includes("green") || s.includes("pos")) return "pos";
  if (s.includes("amber") || s.includes("warn")) return "warn";
  if (s.includes("red") || s.includes("neg")) return "neg";
  return fallback;
}
function arrowFromTag(tag) {
  const s = String(tag || "").trim().toLowerCase();
  if (!s) return "";
  if (s.includes("up") || s.includes("hauss") || s.includes("▲")) return "▲";
  if (s.includes("down") || s.includes("baiss") || s.includes("▼")) return "▼";
  if (s.includes("flat") || s.includes("stable") || s.includes("→")) return "→";
  if (s === "^") return "▲";
  if (s === "v") return "▼";
  return String(tag).trim();
}
function normalizeClusterLabel(v) {
  const raw = String(v || "").trim();
  if (!raw) return "";
  return raw
    .split(/__+/)
    .map((x) => x.replace(/_/g, " ").toLowerCase())
    .join(" | ");
}
function fmtCur(n, ccy = "EUR") {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return `${fmtNum(Number(n), 2)} ${ccy}`;
}
function fmtX(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return `${fmtNum(Number(n), 1)}×`;
}

const CANONICAL_BACKTEST_NOTIONAL = 10000;

/** Première ligne de la série backtest : capital pour les ratios (inchangé côté série). */
function initialNotionalFromTrackFirstRow(first) {
  if (!first) return null;
  const cs = Number(first.capital_start);
  if (Number.isFinite(cs) && cs > 0) return cs;
  const cst = Number(first.capital_start_tr);
  if (Number.isFinite(cst) && cst > 0) return cst;
  return null;
}

function initialAmountForDisplay(raw) {
  if (raw == null || !Number.isFinite(Number(raw))) return null;
  return Number(raw);
}

/** API amounts: large values = EUR; smaller = already in millions (M€). */
function toDisplayMillions(n) {
  if (n == null || !Number.isFinite(Number(n))) return null;
  const v = Number(n);
  if (Math.abs(v) >= 1e7) return v / 1e6;
  return v;
}
function formatMegaM(n) {
  const m = toDisplayMillions(n);
  if (m == null) return "—";
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(m);
}
function formatMegaMFromDisplay(m) {
  if (m == null || !Number.isFinite(Number(m))) return "—";
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Number(m));
}
function efStatusFromMult(mult, needM) {
  // If there is no financing need on this line item, render as N/A (not covered/not uncovered).
  if (needM == null || needM <= 0) return "na";
  if (mult == null || !Number.isFinite(mult)) return "insufficient";
  if (mult >= 1) return "covered";
  if (mult >= 0.5) return "insufficient";
  return "uncovered";
}
function efBadgeToneFromPct(pct) {
  if (pct == null || !Number.isFinite(pct)) return "neutral";
  if (pct >= 100) return "pos";
  if (pct >= 50) return "warn";
  return "neg";
}
const EF_STATUS_STYLES = {
  covered: { mult: C.green, bg: `${C.green}18`, fg: C.green, key: "efCovered" },
  insufficient: { mult: C.orange, bg: `${C.orange}18`, fg: C.orange, key: "efInsufficient" },
  uncovered: { mult: C.red, bg: `${C.red}18`, fg: C.red, key: "efUncovered" },
  na: { mult: "var(--muted)", bg: "rgba(148,163,184,.14)", fg: "var(--muted)", key: "efNA" },
};
function toneFor(n) {
  if (n == null || !Number.isFinite(Number(n))) return "var(--text-strong)";
  if (Number(n) > 0) return C.green;
  if (Number(n) < 0) return C.red;
  return "var(--text-strong)";
}

function hexToRgb(h) {
  const f = String(h || "#000").replace("#", "");
  const n = parseInt(f.length === 3 ? f.split("").map((c) => c + c).join("") : f, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function rgbToHex(r, g, b) {
  const cl = (x) => Math.max(0, Math.min(255, Math.round(x)));
  return `#${cl(r).toString(16).padStart(2, "0")}${cl(g).toString(16).padStart(2, "0")}${cl(b).toString(16).padStart(2, "0")}`;
}
function lerp(a, b, t) {
  return a + (b - a) * t;
}
function lerpColor(a, b, t) {
  const ac = hexToRgb(a);
  const bc = hexToRgb(b);
  return rgbToHex(lerp(ac.r, bc.r, t), lerp(ac.g, bc.g, t), lerp(ac.b, bc.b, t));
}
const HEAT_STOPS = ["#c6523f", "#d28a52", "#caa85e", "#a9b38a", "#8da57a", "#6f8a5b"];
function colorForPct(pct) {
  const t = Math.max(0, Math.min(1, Number(pct || 0) / 100));
  const seg = (HEAT_STOPS.length - 1) * t;
  const i = Math.min(HEAT_STOPS.length - 2, Math.floor(seg));
  return lerpColor(HEAT_STOPS[i], HEAT_STOPS[i + 1], seg - i);
}
function shade(hex, t) {
  const b = hexToRgb(hex);
  const mix = t >= 0 ? { r: 255, g: 255, b: 255 } : { r: 0, g: 0, b: 0 };
  const tt = Math.abs(t);
  return rgbToHex(lerp(b.r, mix.r, tt), lerp(b.g, mix.g, tt), lerp(b.b, mix.b, tt));
}

function distScoreTone(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return "neutral";
  if (n >= 70) return "pos";
  if (n >= 40) return "warn";
  return "neg";
}

function formatDistPayout(payoutRatio, payoutRatioX) {
  if (payoutRatioX != null && Number.isFinite(Number(payoutRatioX))) return `${fmtNum(Number(payoutRatioX), 2)}x`;
  if (payoutRatio == null || !Number.isFinite(Number(payoutRatio))) return "—";
  const v = Number(payoutRatio);
  const av = Math.abs(v);
  if (av > 1.25) return `${fmtNum(v, 2)}x`;
  return `${fmtNum(av * 100, 2)}%`;
}

function formatDistFcf(x) {
  if (x == null || !Number.isFinite(Number(x))) return "—";
  return `${fmtNum(Number(x), 2)}x`;
}

function formatDistGrowth5y(g) {
  if (g == null || !Number.isFinite(Number(g))) return "—";
  const v = Number(g);
  if (Math.abs(v) < 1e-12) return `${fmtNum(0, 2)}%`;
  const pct = Math.abs(v) <= 1 ? v * 100 : v;
  return `${fmtNum(pct, 2)}%`;
}

function fcfCoverColor(x) {
  if (x == null || !Number.isFinite(Number(x))) return "var(--muted)";
  const v = Number(x);
  if (v >= 1.5) return C.green;
  if (v >= 1) return C.amber;
  return C.red;
}

function payoutColor(pr, prX) {
  if (prX != null && Number.isFinite(Number(prX))) {
    const v = Number(prX);
    if (v <= 0.75) return C.green;
    if (v <= 1.5) return C.amber;
    return C.red;
  }
  if (pr == null || !Number.isFinite(Number(pr))) return "var(--muted)";
  const av = Math.abs(Number(pr));
  if (av <= 0.75) return C.green;
  if (av <= 1) return C.amber;
  return C.red;
}

function yieldColor(yieldFrac) {
  if (yieldFrac == null || !Number.isFinite(Number(yieldFrac))) return "var(--muted)";
  const y = Math.abs(Number(yieldFrac)) <= 1 ? Number(yieldFrac) * 100 : Number(yieldFrac);
  if (y >= 4) return C.green;
  if (y >= 2) return lerpColor(C.amber, C.green, 0.45);
  if (y >= 0.5) return C.amber;
  return "var(--text-strong)";
}

function DistributionMonthPill({ label, paid, total, currency, payment }) {
  const payStr =
    payment && String(payment).length >= 10
      ? `pmt ${String(payment).slice(8, 10)}/${String(payment).slice(5, 7)}`
      : null;
  return (
    <div
      className="dist-month-pill"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: 12,
        padding: "8px 10px",
        minHeight: 44,
        background: paid ? `linear-gradient(135deg, ${shade(C.green, 0.12)} 0%, ${C.green} 100%)` : "var(--summary-bg)",
        border: `1px solid ${paid ? shade(C.green, 0.22) : "var(--sep)"}`,
      }}
    >
      <span
        style={{
          fontSize: "clamp(10px, 2.8vw, 11.5px)",
          fontWeight: 800,
          letterSpacing: "0.04em",
          color: paid ? "rgba(255,255,255,0.95)" : "var(--muted)",
        }}
      >
        {label}
      </span>
      {paid ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
          <span style={{ fontFamily: "ui-monospace,monospace", fontSize: "clamp(9px, 2.5vw, 10px)", fontWeight: 700, color: "rgba(255,255,255,0.92)" }}>
            +{fmtNum(total, 2)} {currency}
          </span>
          {payStr ? (
            <span style={{ fontSize: "clamp(7px, 2.1vw, 8.5px)", color: "rgba(255,255,255,0.55)" }}>{payStr}</span>
          ) : null}
        </div>
      ) : (
        <span style={{ fontSize: 10, color: "var(--muted)", opacity: 0.45 }}>—</span>
      )}
    </div>
  );
}

function DistMetricTile({ label, value, color, tip }) {
  return (
    <div
      className="dist-metric-tile"
      style={{
        borderRadius: 12,
        border: "1px solid var(--sep)",
        background: "var(--summary-bg)",
        padding: "10px 12px",
        minHeight: 72,
      }}
    >
      <div
        style={{
          color: "var(--muted)",
          fontSize: "clamp(9px, 2.4vw, 11px)",
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          fontWeight: 700,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {label}
        {tip ? <TipMark title={tip} /> : null}
      </div>
      <div style={{ marginTop: 4, color: color || "var(--text-strong)", fontSize: "clamp(13px, 3.2vw, 15px)", fontWeight: 800, fontFamily: "ui-monospace,monospace", lineHeight: 1.15 }}>{value}</div>
    </div>
  );
}

function normalizeScore(v) {
  if (v == null || !Number.isFinite(Number(v))) return 50;
  const n = Number(v);
  if (Math.abs(n) <= 1) return Math.max(0, Math.min(100, Math.round(n * 100)));
  return Math.max(0, Math.min(100, Math.round(n)));
}

function normalizeTickerPayload(raw, symbol) {
  if (!raw || typeof raw !== "object") return raw;
  if (raw.company || raw.valuation || raw.priceChart || raw.backtestMeta) return raw;

  const r = raw;
  const company = {
    name: r.company_name || r.name || symbol,
    symbol: r.symbol || symbol,
    exchange: r.exchange || r.market || "",
    sector: r.sector || "",
    industry: r.industry || "",
    region: r.region || "",
    country: r.country || "",
    marketCapClass: r.market_cap_class || r.mkt_class || "",
    style: r.stock_style || r.style || "",
    cyclicals: r.cycle_profile_name || r.cycle_name || r.cyclicals || "",
    description: r.description || r.company_description || null,
    currency: r.currency || r.intrinsic_currency || "EUR",
  };

  const valuation = {
    currentPrice: r.close_price_local ?? r.price ?? r.current_price ?? null,
    dcfValue: r.dcf_value ?? r.val_dcf_value ?? null,
    mosPct: r.Margin_of_safety ?? r.mos_pct ?? null,
    valuationConsensus: r.valuation_interpretation || r.valuation_status || null,
    peRatio: r.pe_ttm ?? null,
    pbRatio: r.pb_ttm ?? null,
    evEbitda: r.ev_to_ebitda_ttm ?? null,
    psRatio: r.ps_ttm ?? null,
    pcf: r.pfcf_ttm ?? null,
    fcfYieldPct: r.fcf_yield ?? null,
    upsideBeta: r.upside_dcf_local_pct ?? r.composite_upside_local_pct ?? null,
    valuationDate: r.valuation_date || null,
    valuationMessage: r.valuation_summary || r.valuation_narrative || null,
    avgPe5y: r.pe_5y_avg ?? null,
    avgPb5y: r.pb_5y_avg ?? null,
    avgEvEbitda5y: r.ev_to_ebitda_5y_avg ?? null,
    avgPs5y: r.ps_5y_avg ?? null,
    avgPfcf5y: r.pfcf_5y_avg ?? null,
    currency: company.currency,
  };

  const timingChart = Array.isArray(r.timing_chart_json)
    ? r.timing_chart_json
    : (typeof r.timing_chart_json === "string" && r.timing_chart_json.trim()
      ? (() => {
          try {
            return JSON.parse(r.timing_chart_json);
          } catch {
            return [];
          }
        })()
      : []);

  return {
    ...r,
    company,
    price: r.price ?? r.close_price_local ?? r.current_price ?? null,
    mtdPct: r.mtd_pct ?? null,
    ytdPct: r.ytd_pct ?? null,
    iScore: r.iscore ?? r.iScore ?? null,
    radar: [
      { axis: "Fondamentaux", value: r.radar_fondamentaux ?? r.fund_score ?? null },
      { axis: "Valorisation", value: r.radar_valorisation ?? r.valuation_score ?? null },
      { axis: "Marché", value: r.radar_marche ?? r.dd_marche_score ?? null },
      { axis: "Momentum", value: r.radar_momentum ?? r.momentum_score ?? null },
      { axis: "Risque/Rend.", value: r.radar_risque_rendement ?? r.rr_irrs_score ?? null },
    ].filter((x) => x.value != null),
    valuation,
    timing: {
      trendLabel: r.timing_trend_direction ?? null,
      momentumScore: r.momentum_score ?? null,
      timingSignalLabel: r.timing_volume_label ?? null,
      timingSignal: r.timing_volume_label ?? null,
      timingNarrative: r.timing_summary || r.timing_narrative || null,
      sma20: r.timing_sma20 ?? null,
      sma50: r.timing_sma50 ?? null,
      sma200: r.timing_sma200 ?? null,
    },
    priceChart: timingChart.map((p) => ({
      date: p.date ?? p.Date ?? "",
      close: p.close ?? p.c ?? null,
      sma20: p.sma20 ?? null,
      sma50: p.sma50 ?? null,
      sma200: p.sma200 ?? null,
      volume: p.volume ?? p.v ?? null,
    })),
  };
}
function statusTone(label) {
  const s = String(label || "").toLowerCase();
  if (s.includes("good") || s.includes("favorable") || s.includes("hauss") || s.includes("underval")) return "pos";
  if (s.includes("bad") || s.includes("défavorable") || s.includes("baiss") || s.includes("overval") || s.includes("suréval")) return "neg";
  if (s.includes("watch") || s.includes("moyen") || s.includes("modér") || s.includes("neutral") || s.includes("insuff") || s.includes("fragile")) return "warn";
  return "neutral";
}

function inferDecisionTone(label, score, fallbackTone) {
  const s = String(label || "").toLowerCase();
  // Strong negatives should always be red/orange.
  if (
    s.includes("bad") ||
    s.includes("worst") ||
    s.includes("losing") ||
    s.includes("deterior") ||
    s.includes("défavorable") ||
    s.includes("baissier")
  ) {
    return "neg";
  }
  // Warning zone keywords.
  if (
    s.includes("watch") ||
    s.includes("vigil") ||
    s.includes("déséquilibr") ||
    s.includes("desequilibr") ||
    s.includes("risk")
  ) {
    return "warn";
  }
  // Positive/trend labels (even with low numeric score in source data).
  if (
    s.includes("favorable") ||
    s.includes("haussier") ||
    s.includes("underval") ||
    s.includes("good") ||
    s.includes("distribution") ||
    s.includes("expansion") ||
    s.includes("innovation")
  ) {
    return "pos";
  }
  // Fallback on numeric score only if label has no semantic cue.
  const n = Number(score);
  if (Number.isFinite(n)) {
    if (n >= 60) return "pos";
    if (n >= 35) return "warn";
    if (n >= 0) return "neg";
  }
  return fallbackTone || "neutral";
}

function Card({ children, className }) {
  return (
    <div
      className={className}
      style={{
        background: "var(--card-bg)",
        border: "1px solid var(--card-border)",
        borderRadius: 20,
        boxShadow: "0 1px 3px rgba(15,23,42,.08)",
        backdropFilter: "blur(8px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {children}
    </div>
  );
}
function Sep() {
  return <div style={{ height: 1, marginTop: 10, background: "var(--sep)" }} />;
}
function CardHead({ title, subtitle, right }) {
  return (
    <div style={{ padding: "16px 20px 0" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ color: "var(--text-strong)", fontSize: 16, fontWeight: 600, letterSpacing: "-0.01em" }}>{title}</div>
          {subtitle ? <div style={{ marginTop: 4, color: "#b79a51", fontSize: 14, fontWeight: 500 }}>{subtitle}</div> : null}
        </div>
        {right}
      </div>
      <Sep />
    </div>
  );
}
function StatusBadge({ children, tone = "neutral" }) {
  const map = {
    pos: { bg: `${C.green}18`, border: `${C.green}44`, color: C.green },
    neg: { bg: `${C.red}14`, border: `${C.red}44`, color: C.red },
    warn: { bg: `${C.amber}1f`, border: `${C.amber}66`, color: C.amber },
    neutral: { bg: "var(--badge-bg)", border: "rgba(181,154,91,.28)", color: "var(--badge-fg)" },
  };
  const s = map[tone] || map.neutral;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        padding: "4px 12px",
        border: `1px solid ${s.border}`,
        background: s.bg,
        color: s.color,
        fontWeight: 600,
        fontSize: 11,
      }}
    >
      {children}
    </span>
  );
}
function SummaryBox({ children }) {
  return (
    <div style={{ marginTop: 12, borderRadius: 12, padding: "12px 16px", background: "var(--summary-bg)", fontSize: 12, lineHeight: 1.35, color: "var(--text)" }}>
      {children}
    </div>
  );
}
function MiniTile({ label, value, sub, color }) {
  return (
    <div
      style={{
        borderRadius: 12,
        border: "1px solid var(--sep)",
        background: "var(--summary-bg)",
        padding: "8px 12px",
        minHeight: 66,
      }}
    >
      <div style={{ color: "var(--muted)", fontSize: 12, textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 700, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{label}</div>
      <div style={{ marginTop: 2, color: color || "var(--text-strong)", fontSize: 13, fontWeight: 700, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", lineHeight: 1 }}>{value}</div>
      {sub ? <div style={{ marginTop: 3, color: "var(--muted)", fontSize: 10 }}>{sub}</div> : null}
    </div>
  );
}
function DecisionRow({ label, score, tone, tip }) {
  const palette = {
    pos: { bg: "linear-gradient(135deg,#a5b88f 0%,#8ea77a 100%)", chip: "rgba(255,255,255,.24)", chipColor: "#6f8a5b", icon: CheckCircle2 },
    neg: { bg: "linear-gradient(135deg,#d7a07f 0%,#cd8767 100%)", chip: "rgba(255,255,255,.22)", chipColor: "#c6523f", icon: XCircle },
    warn: { bg: "linear-gradient(135deg,#cbc497 0%,#bbb381 100%)", chip: "rgba(255,255,255,.22)", chipColor: "#b3924e", icon: AlertTriangle },
    neutral: { bg: "linear-gradient(135deg,#b9c7a3 0%,#a3b28a 100%)", chip: "rgba(255,255,255,.22)", chipColor: "#6f8a5b", icon: CheckCircle2 },
  };
  const p = palette[tone] || palette.neutral;
  const Icon = p.icon;
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, borderRadius: 20, padding: "10px 12px", background: p.bg, border: "1px solid rgba(255,255,255,.28)", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
        <span style={{ width: 24, height: 24, borderRadius: 999, background: "rgba(255,255,255,.24)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon size={13} color="rgba(255,255,255,.95)" />
        </span>
        <div style={{ color: "var(--text-strong)", fontWeight: 700, fontSize: 12, lineHeight: 1.25, whiteSpace: "nowrap", textOverflow: "ellipsis", display: "flex", alignItems: "center", gap: 6, minWidth: 0, overflow: "hidden" }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
          {tip ? <TipMark title={tip} /> : null}
        </div>
      </div>
      <span style={{ borderRadius: 999, padding: "3px 9px", background: p.chip, color: p.chipColor, fontWeight: 800, fontFamily: "ui-monospace,monospace", fontSize: 12 }}>{score}</span>
    </div>
  );
}
function CTA({ onClick, label }) {
  return (
    <div style={{ marginTop: "auto", display: "flex", justifyContent: "flex-end", paddingTop: 10 }}>
      <button
        type="button"
        onClick={onClick}
        className="ticker-cta-btn"
        style={{
          border: "1px solid var(--cta-border)",
          borderRadius: 12,
          background: "var(--cta-bg)",
          color: "var(--cta-fg)",
          padding: "8px 14px",
          fontSize: 14,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        {label}
      </button>
    </div>
  );
}

function GaugeSemi({ ratio, topLabel, bottomLabel, axisLabel, cornerLeft, cornerRight }) {
  const W = 320;
  const H = 206;
  const cx = W / 2;
  const cy = H - 28;
  const r = 118;
  const clamped = Math.max(0.03, Math.min(0.97, Number.isFinite(ratio) ? ratio : 0.5));
  const angle = Math.PI * (1 - clamped);
  const nx = cx + r * Math.cos(angle);
  const ny = cy - r * Math.sin(angle);
  const segments = [
    { a0: Math.PI, a1: Math.PI * 0.8, color: "#6f8a5b" },
    { a0: Math.PI * 0.8, a1: Math.PI * 0.6, color: "#a9b38a" },
    { a0: Math.PI * 0.6, a1: Math.PI * 0.4, color: "#caa85e" },
    { a0: Math.PI * 0.4, a1: Math.PI * 0.2, color: "#d28a52" },
    { a0: Math.PI * 0.2, a1: 0, color: "#c6523f" },
  ];
  const arc = (a0, a1) => {
    const x0 = cx + r * Math.cos(a0);
    const y0 = cy - r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1);
    const y1 = cy - r * Math.sin(a1);
    return `M ${x0} ${y0} A ${r} ${r} 0 0 1 ${x1} ${y1}`;
  };
  const ax = axisLabel || "Juste valeur";
  const cL = cornerLeft || "Faible";
  const cR = cornerRight || "Élevée";
  return (
    <div style={{ marginTop: 2 }}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`}>
        {segments.map((s, i) => (
          <path key={i} d={arc(s.a0, s.a1)} fill="none" stroke={s.color} strokeWidth="16" strokeLinecap="round" />
        ))}
        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="var(--gauge-needle)" strokeWidth="6" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="9" fill="var(--gauge-needle)" />
        <text x={cx} y={30} textAnchor="middle" fontSize="11" fill="var(--muted)" fontFamily="ui-monospace,monospace">
          {ax}
        </text>
        <text x={cx} y={52} textAnchor="middle" fontSize="19" fontWeight="700" fill="var(--text-strong)" fontFamily="ui-monospace,monospace">{topLabel}</text>
        <text x={cx} y={116} textAnchor="middle" fontSize="11" fill="var(--muted)" fontFamily="ui-monospace,monospace">{bottomLabel}</text>
        <text x={26} y={140} textAnchor="start" fontSize="10" fill="var(--muted)">
          {cL}
        </text>
        <text x={294} y={140} textAnchor="end" fontSize="10" fill="var(--muted)">
          {cR}
        </text>
      </svg>
    </div>
  );
}

function RiskQuadrant({ cagrPct, drawdownPct, symbol }) {
  const W = 360;
  const H = 200;
  const PAD = { l: 38, r: 18, t: 14, b: 30 };
  const minX = 0;
  const maxX = 60;
  const minY = -15;
  const maxY = 20;
  const SPLIT_X = 30;
  const SPLIT_Y = 6;
  const toX = (x) => PAD.l + ((x - minX) / (maxX - minX)) * (W - PAD.l - PAD.r);
  const toY = (y) => PAD.t + ((maxY - y) / (maxY - minY)) * (H - PAD.t - PAD.b);
  const x = toX(Math.min(maxX - 1, Math.max(minX + 1, Math.abs(Number(drawdownPct || 0)))));
  const y = toY(Math.min(maxY - 0.5, Math.max(minY + 1, Number(cagrPct || 0))));
  const yTicks = [-15, -10, -5, 0, 5, 10, 15, 20];

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`}>
      {/* Quadrants */}
      <rect x={PAD.l} y={PAD.t} width={toX(SPLIT_X) - PAD.l} height={toY(SPLIT_Y) - PAD.t} fill="rgba(111,138,91,.10)" />
      <rect x={toX(SPLIT_X)} y={PAD.t} width={W - PAD.r - toX(SPLIT_X)} height={toY(SPLIT_Y) - PAD.t} fill="rgba(169,179,138,.10)" />
      <rect x={PAD.l} y={toY(SPLIT_Y)} width={toX(SPLIT_X) - PAD.l} height={toY(0) - toY(SPLIT_Y)} fill="rgba(202,168,94,.10)" />
      <rect x={toX(SPLIT_X)} y={toY(SPLIT_Y)} width={W - PAD.r - toX(SPLIT_X)} height={toY(0) - toY(SPLIT_Y)} fill="rgba(210,138,82,.11)" />
      <rect x={PAD.l} y={toY(0)} width={W - PAD.l - PAD.r} height={H - PAD.b - toY(0)} fill="rgba(198,82,63,.12)" />

      {/* Grid */}
      {yTicks.map((t) => (
        <line key={t} x1={PAD.l} x2={W - PAD.r} y1={toY(t)} y2={toY(t)} stroke="var(--grid-line)" strokeWidth={1} />
      ))}
      {[15, 30, 50].map((xTick) => (
        <line key={xTick} x1={toX(xTick)} x2={toX(xTick)} y1={PAD.t} y2={H - PAD.b} stroke="var(--grid-line)" strokeDasharray="3 3" />
      ))}
      <line x1={toX(SPLIT_X)} x2={toX(SPLIT_X)} y1={PAD.t} y2={H - PAD.b} stroke="var(--sep)" strokeDasharray="4 3" />
      <line x1={PAD.l} x2={W - PAD.r} y1={toY(SPLIT_Y)} y2={toY(SPLIT_Y)} stroke="var(--sep)" strokeDasharray="4 3" />

      {/* Labels */}
      <text x={PAD.l + 2} y={toY(17)} fontSize="10" fill="rgba(31,42,36,.32)" fontWeight="700">Efficient</text>
      <text x={toX(SPLIT_X) + 6} y={toY(17)} fontSize="10" fill="rgba(31,42,36,.32)" fontWeight="700">Dynamique</text>
      <text x={PAD.l + 2} y={toY(2.2)} fontSize="10" fill="rgba(31,42,36,.32)" fontWeight="700">Défensif</text>
      <text x={PAD.l + 2} y={toY(-13)} fontSize="10" fill="rgba(31,42,36,.32)" fontWeight="700">Destructeur de capital</text>

      {/* Axes ticks */}
      {yTicks.map((t) => (
        <text key={`y-${t}`} x={PAD.l - 8} y={toY(t) + 3} textAnchor="end" fontSize="9" fill="var(--muted)" fontFamily="ui-monospace,monospace">
          {t > 0 ? `+${t}%` : `${t}%`}
        </text>
      ))}
      {[0, 15, 25, 35, 50, 60].map((xTick) => (
        <text key={`x-${xTick}`} x={toX(xTick)} y={H - PAD.b + 14} textAnchor="middle" fontSize="9" fill="var(--muted)" fontFamily="ui-monospace,monospace">
          {xTick}%
        </text>
      ))}

      {/* Point */}
      <circle cx={x} cy={y} r="7" fill="#d28a52" />
      <circle cx={x} cy={y} r="11" fill="rgba(210,138,82,.14)" />
    </svg>
  );
}

function HHIBar({ value }) {
  const raw = Number(value);
  const max = 10000;
  const v = Number.isFinite(raw) ? Math.max(0, Math.min(max, raw)) : 0;
  const h = 154;
  const y = h - (v / max) * h;
  const concTone = v < 1500 ? C.green : v < 2500 ? C.amber : C.red;
  const fillColor = concTone;
  const fillHeight = Math.max(6, h - y);
  const th1500 = 24 + h * 0.85;
  const th2500 = 24 + h * 0.75;
  const markerY = 24 + y;
  const labelText = v < 1500 ? "Fragmente" : v < 2500 ? "Modere" : "Concentre";
  return (
    <svg width="96" height="192" viewBox="0 0 96 192">
      <text x="6" y="16" fontSize="9" fill="var(--muted)" fontFamily="ui-monospace,monospace">IHH</text>
      <text x="26" y="16" fontSize="18" fontWeight="800" fill={concTone} fontFamily="ui-monospace,monospace">{Math.round(v)}</text>
      {/* Zone background (reference scale 0-10k) */}
      <rect x="8" y="24" width="34" height={h} rx="10" fill="#ecebe6" />
      <rect x="8" y={24 + h * 0.85} width="34" height={h * 0.15} rx="8" fill={C.green} opacity="0.55" />
      <rect x="8" y={24 + h * 0.75} width="34" height={h * 0.10} fill={C.amber} opacity="0.55" />
      <rect x="8" y="24" width="34" height={h * 0.75} rx="10" fill={C.red} opacity="0.55" />

      {/* Dynamic fill driven by HHI value (kept semi-transparent to preserve zones) */}
      <rect x="9.5" y={24 + y} width="31" height={fillHeight} rx="8" fill={fillColor} opacity="0.58" />
      {/* Threshold separators always visible */}
      <line x1="8" x2="42" y1={th2500} y2={th2500} stroke="rgba(255,255,255,.78)" strokeWidth="1.2" />
      <line x1="8" x2="42" y1={th1500} y2={th1500} stroke="rgba(255,255,255,.78)" strokeWidth="1.2" />

      {/* Marker */}
      <line x1="6" x2="44" y1={markerY} y2={markerY} stroke="white" strokeWidth="2.3" />
      <circle cx="6" cy={markerY} r="4" fill="white" />
      <text x="48" y={Math.max(34, markerY - 4)} fontSize="9.5" fill={concTone} fontFamily="ui-monospace,monospace">{Math.round(v)}</text>

      {/* Non-overlapping labels */}
      <text x="48" y="166" fontSize="8.5" fill={C.red} fontFamily="ui-monospace,monospace">Concentre</text>
      <text x="48" y="178" fontSize="8.5" fill={C.amber} fontFamily="ui-monospace,monospace">Modere</text>
      <text x="48" y="190" fontSize="8.5" fill={C.green} fontFamily="ui-monospace,monospace">Fragmente</text>
      <text x="8" y="190" fontSize="8.5" fill={concTone} fontWeight="700" fontFamily="ui-monospace,monospace">{labelText}</text>
    </svg>
  );
}

function TipMark({ title: tip }) {
  const [open, setOpen] = useState(false);
  const isMobile = typeof window !== "undefined" ? window.matchMedia("(max-width: 820px)").matches : false;
  if (!tip) return null;
  return (
    <>
      <button
        type="button"
        title={tip}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 16,
          height: 16,
          marginLeft: 4,
          borderRadius: 999,
          border: "1px solid rgba(128,138,149,.55)",
          fontSize: 10,
          fontWeight: 800,
          color: "rgba(90,103,114,.95)",
          cursor: "help",
          verticalAlign: "middle",
          lineHeight: 1,
          background: "transparent",
          padding: 0,
        }}
      >
        i
      </button>
      {open ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            background: "rgba(9,15,29,.38)",
            display: "flex",
            alignItems: isMobile ? "flex-end" : "center",
            justifyContent: "center",
            padding: isMobile ? "0" : "14px",
          }}
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: isMobile ? "100%" : "min(760px, calc(100vw - 28px))",
              maxHeight: isMobile ? "74vh" : "min(82vh, 680px)",
              overflowY: "auto",
              borderRadius: isMobile ? "16px 16px 0 0" : "16px",
              background: "var(--card-bg)",
              border: "1px solid var(--card-border)",
              boxShadow: isMobile ? "0 -16px 36px rgba(0,0,0,.22)" : "0 16px 36px rgba(0,0,0,.22)",
              padding: "12px 14px 16px",
              boxSizing: "border-box",
            }}
          >
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{
                  border: "1px solid var(--card-border)",
                  borderRadius: 10,
                  background: "transparent",
                  color: "var(--text)",
                  padding: "5px 10px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Fermer
              </button>
            </div>
            <div
              style={{
                marginTop: 8,
                borderRadius: 12,
                border: "1px solid var(--sep)",
                background: "var(--summary-bg)",
                padding: "12px 14px",
                color: "var(--text)",
                fontSize: 15,
                lineHeight: 1.5,
                overflowWrap: "anywhere",
                whiteSpace: "pre-wrap",
              }}
            >
              {tip}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

const SEG_COLORS_EF = [C.green, C.green2, C.amber, C.red];

/** Non-linear horizontal position for low-% emphasis (reference scale under bar). */
function efNonLinearX(pct) {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  return 100 * Math.sqrt(p / 100);
}

function StackedCoverageMeter({ segments, pointerPct, pointerLabel, leftLabel, rightLabel, pointerBarColor, pointerLabelColor }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setReady(true), 250);
    return () => window.clearTimeout(id);
  }, [segments, pointerPct]);
  const total = segments.reduce((s, it) => s + (Number(it.amount) || 0), 0);
  let cum = 0;
  const built = segments.map((it, i) => {
    const amt = Math.max(0, Number(it.amount) || 0);
    const w = total > 0 ? (amt / total) * 100 : 0;
    const left = cum;
    cum += w;
    const base = it.color || SEG_COLORS_EF[i % SEG_COLORS_EF.length];
    const segDark = it.id === "equity" ? shade(base, -0.12) : it.id === "debt" ? shade(base, -0.06) : base;
    return { ...it, w, left, color: segDark, segIndex: i };
  });
  const leftPct = Math.min(100, Math.max(0, Number(pointerPct) || 0));
  const x = ready ? leftPct : 0;
  const barCol = pointerBarColor || "rgba(255,255,255,.95)";
  const lblCol = pointerLabelColor || "var(--text-strong)";
  const nlTicks = [
    { v: 0, lab: "0" },
    { v: 1, lab: "1%" },
    { v: 2, lab: "2%" },
    { v: 10, lab: "10%" },
    { v: 100, lab: "100%" },
  ];

  return (
    <div className="ef-meter-root">
      <div className="ef-meter-axis">
        <span className="ef-meter-axis-l">{leftLabel}</span>
        <span className="ef-meter-axis-r">{rightLabel}</span>
      </div>
      <div className="ef-meter-track-wrap">
        <div className="ef-meter-track">
          {built.map((seg) => (
            <div
              key={seg.id}
              className="ef-meter-seg"
              style={{
                width: `${seg.w}%`,
                background: `linear-gradient(180deg, ${shade(seg.color, 0.08)} 0%, ${seg.color} 55%, ${shade(seg.color, -0.06)} 100%)`,
                filter: seg.id === "bfr" ? "brightness(0.97)" : seg.id === "capex" ? "brightness(1)" : seg.id === "debt" ? "brightness(0.99)" : "brightness(0.9)",
                borderRight: seg.id !== "equity" ? "1.5px solid rgba(255,255,255,0.28)" : "none",
              }}
            >
              <div className="ef-meter-seg-inner">
                {seg.w > 9 ? <span className="ef-meter-seg-lbl">{seg.label}</span> : null}
                {seg.w > 11 ? <span className="ef-meter-seg-val">{formatMegaM(seg.raw)}</span> : null}
              </div>
              <div className="ef-meter-seg-shine" />
            </div>
          ))}
        </div>
        <div className="ef-meter-cursor" style={{ left: `${x}%`, transition: "left 1s cubic-bezier(.4,0,.2,1)" }}>
          <div
            className="ef-meter-cursor-bar"
            style={{
              background: barCol,
              boxShadow: `0 0 0 1px rgba(255,255,255,0.35), 0 2px 8px rgba(0,0,0,.22)`,
            }}
          />
        </div>
      </div>
      <div className="ef-meter-pointer-wrap">
        <div
          className="ef-meter-pointer-lbl"
          style={{
            left: `${x}%`,
            transition: "left 1s cubic-bezier(.4,0,.2,1)",
            color: lblCol,
            border: `1px solid rgba(30,40,34,.08)`,
          }}
        >
          {pointerLabel}
        </div>
      </div>
      <div className="ef-meter-ticks-nl" aria-hidden>
        {nlTicks.map((tk) => (
          <span key={`nl-${tk.v}`} className="ef-meter-tick-nl" style={{ left: `${efNonLinearX(tk.v)}%` }}>
            {tk.lab}
          </span>
        ))}
      </div>
    </div>
  );
}

function EFMetricCell({ label, tip, amountRaw, mult, status, t }) {
  const st = EF_STATUS_STYLES[status] || EF_STATUS_STYLES.insufficient;
  const badgeLabel = t(st.key);
  return (
    <div className="ef-cell">
      <div className="ef-cell-top">
        <span className="ef-cell-lbl">
          {label}
          {tip ? <TipMark title={tip} /> : null}
        </span>
        <span className="ef-cell-amt">{formatMegaM(amountRaw)} M€</span>
      </div>
      <div className="ef-cell-bot">
        <span className="ef-cell-mult" style={{ color: mult != null && Number.isFinite(Number(mult)) ? st.mult : "var(--muted)" }}>
          {status === "na" ? (
            "—"
          ) : mult != null && Number.isFinite(Number(mult)) ? (
            <>
              <span className="ef-cell-arrow">{Number(mult) >= 1 ? "▲" : "▼"}</span>
              {`${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(Math.abs(Number(mult)))}×`}
            </>
          ) : (
            "—"
          )}
        </span>
        <span className="ef-cell-badge" style={{ background: st.bg, color: st.fg }}>
          {badgeLabel}
        </span>
      </div>
    </div>
  );
}

function TickerAnalysis() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [language, setLanguage] = useState(getLanguage());
  const [activeAnalysis, setActiveAnalysis] = useState(null);
  const [expandedDesc, setExpandedDesc] = useState(false);
  const [darkMode, setDarkMode] = useState(() => getStoredDarkMode());
  const [activeFundTip, setActiveFundTip] = useState(null);
  const t = (k) => (I18N[language] && I18N[language][k]) || I18N.fr[k] || k;
  const navFrom = location?.state?.from === "leaders" ? "leaders" : "marketflow";
  const backPath = navFrom === "leaders" ? "/" : "/marketflow";
  const backLabel = navFrom === "leaders" ? t("backToMarketLeaders") : t("backToMarketFlow");

  useEffect(() => {
    applyTheme(darkMode);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, darkMode ? "dark" : "light");
    } catch {
      /* ignore */
    }
  }, [darkMode]);

  useEffect(() => {
    const sync = () => setLanguage(getLanguage());
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);
    const id = window.setInterval(sync, 1200);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
      window.clearInterval(id);
    };
  }, []);

  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    const isLocalhost = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
    const baseUrl = isLocalhost ? "http://127.0.0.1:8000/api" : Constants.BACKEND_SERVER_BASE_URL;
    fetch(`${baseUrl}/capitalflow/ticker/${encodeURIComponent(symbol)}`)
      .then((res) => (res.ok ? res.json() : res.json().then((b) => Promise.reject(new Error(b?.error || `HTTP ${res.status}`)))))
      .then((json) => {
        if (!cancelled) setData(normalizeTickerPayload(json, symbol));
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || "Erreur de chargement");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [symbol]);

  const c = data?.company || {};
  const displayCurrency = c.currency || data?.valuation?.currency || data?.distribution?.currency || "EUR";
  const radarData = useMemo(() => {
    const r = Array.isArray(data?.radar) ? data.radar : [];
    if (!r.length) {
      return [
        { axis: "Fondamentaux", value: normalizeScore(data?.iScore) },
        { axis: "Valorisation", value: normalizeScore(data?.valuation?.score) },
        { axis: "Marché", value: normalizeScore(data?.marketStructure?.score) },
        { axis: "Momentum", value: normalizeScore(data?.timing?.momentumScore) },
        { axis: "Risque/Rend.", value: normalizeScore(data?.riskReturn?.sharpe ? data.riskReturn.sharpe * 20 : null) },
      ];
    }
    return r.map((x) => ({ axis: x.axis, value: normalizeScore(x.value) }));
  }, [data]);

  const chartData = useMemo(() => {
    const arr = Array.isArray(data?.priceChart) ? data.priceChart : [];
    return arr.map((p, i) => ({
      i,
      close: Number(p.close),
      sma20: Number(p.sma20),
      sma50: Number(p.sma50),
      sma200: Number(p.sma200),
      vol: Number(p.volume),
      lbl: String(p.date || "").slice(5, 10),
      date: String(p.date || ""),
    }));
  }, [data]);

  const backtestData = useMemo(() => {
    const arr = Array.isArray(data?.backtest) ? data.backtest : [];
    if (!arr.length) return [];
    const first = arr[0];
    const init = initialNotionalFromTrackFirstRow(first);
    return arr.map((r) => {
      const endTr = Number(r.capital_end_tr);
      const endPx = Number(r.capital_end);
      const endBench = Number(r.index_tr);
      return {
        x: String(r.track_year ?? ""),
        trMult: init != null && init > 0 && Number.isFinite(endTr) ? endTr / init : null,
        pxMult: init != null && init > 0 && Number.isFinite(endPx) ? endPx / init : null,
        benchMult: init != null && init > 0 && Number.isFinite(endBench) ? endBench / init : null,
      };
    });
  }, [data]);

  const backtestMetaResolved = useMemo(() => {
    const m = data?.backtestMeta;
    if (m != null && typeof m === "object" && Object.keys(m).length > 0) {
      return { ...m, initialAmount: initialAmountForDisplay(m.initialAmount) };
    }
    const arr = Array.isArray(data?.backtest) ? data.backtest : [];
    if (!arr.length) return null;
    const first = arr[0];
    const last = arr[arr.length - 1];
    const init = initialNotionalFromTrackFirstRow(first);
    const finalTr = Number(last?.capital_end_tr);
    const finalPx = Number(last?.capital_end);
    const divs = Number(last?.cumulative_dividends);
    return {
      initialAmount: initialAmountForDisplay(init),
      dividendsReceived: Number.isFinite(divs) ? divs : null,
      gainAmount: init != null && Number.isFinite(finalTr) ? finalTr - init : null,
      finalAmount: Number.isFinite(finalTr) ? finalTr : null,
      finalMultiple: init != null && init > 0 && Number.isFinite(finalPx) ? finalPx / init : null,
      finalTrMultiple: init != null && init > 0 && Number.isFinite(finalTr) ? finalTr / init : null,
      cagrPrice: null,
      cagrTr: null,
      cagrBenchmark: null,
      xPrice: init != null && init > 0 && Number.isFinite(finalPx) ? finalPx / init : null,
      xTr: init != null && init > 0 && Number.isFinite(finalTr) ? finalTr / init : null,
      maxDrawdown: null,
      benchmark: last?.benchmark_code || null,
      periodLabel: first?.start_year && last?.ref_year ? `${first.start_year}–${last.ref_year}` : null,
      summary: null,
    };
  }, [data]);

  const decisionItems = useMemo(() => {
    const mapped = Array.isArray(data?.decisionDrivers) ? data.decisionDrivers : [];
    if (mapped.length) {
      return mapped.map((it) => {
        const score = normalizeScore(it?.score);
        const status = String(it?.status || "").toLowerCase();
        const toneFromStatus =
          status.includes("pos") || status.includes("favo") || status.includes("green")
            ? "pos"
            : status.includes("neg") || status.includes("defa") || status.includes("red")
              ? "neg"
              : status.includes("warn") || status.includes("vigil") || status.includes("amber")
                ? "warn"
                : null;
        return {
          label: it?.label || "—",
          score,
          tone: toneFromStatus || inferDecisionTone(it?.label, score, "neutral"),
          tip: it?.tooltip || null,
        };
      });
    }
    const raw = [
      { label: data?.marketStructure?.marketPhase || "Marché", score: normalizeScore(data?.marketStructure?.score), tone: statusTone(data?.marketStructure?.marketPhase), tip: null },
      { label: data?.timing?.trendLabel || "Timing", score: normalizeScore(data?.timing?.momentumScore), tone: statusTone(data?.timing?.trendLabel), tip: null },
      { label: data?.valuation?.valuationConsensus || "Valorisation", score: normalizeScore(data?.valuation?.score), tone: statusTone(data?.valuation?.valuationConsensus), tip: null },
      { label: data?.riskReturn?.quadrant || "Risque / Rendement", score: normalizeScore((data?.riskReturn?.sharpe || 0) * 20), tone: statusTone(data?.riskReturn?.quadrant), tip: null },
      { label: data?.fundamental || "Fondamentaux", score: normalizeScore(data?.iScore), tone: statusTone(data?.fundamental), tip: null },
      { label: data?.marketStructure?.marketFlow || "Flux", score: normalizeScore(data?.distribution?.dividendScore), tone: statusTone(data?.marketStructure?.marketFlow), tip: null },
    ];
    return raw.map((it) => ({
      ...it,
      tone: inferDecisionTone(it.label, it.score, it.tone),
    }));
  }, [data]);
  const decisionScore = useMemo(() => {
    if (!decisionItems.length) return 0;
    return Math.round(decisionItems.reduce((acc, it) => acc + Number(it.score || 0), 0) / decisionItems.length);
  }, [decisionItems]);
  const decisionTone = useMemo(() => (decisionScore >= 65 ? "pos" : decisionScore >= 45 ? "warn" : "neg"), [decisionScore]);
  const yesCount = useMemo(() => decisionItems.filter((it) => it.tone === "pos").length, [decisionItems]);
  const watchCount = useMemo(() => decisionItems.filter((it) => it.tone === "warn").length, [decisionItems]);
  const noCount = useMemo(() => decisionItems.filter((it) => it.tone === "neg").length, [decisionItems]);

  const valuationState = useMemo(() => {
    const dcf = Number(data?.valuation?.dcfValue);
    const price = Number(data?.valuation?.currentPrice || data?.price);
    const mos = Number(data?.valuation?.mosPct);
    const consensus = String(data?.valuation?.valuationConsensus || "").toLowerCase();

    const labelFor = (bucket) => {
      const fr = {
        veryUndervalued: "Très sous-évalué",
        undervalued: "Sous-évalué",
        fair: "Juste valeur",
        overvalued: "Surévalué",
        veryOvervalued: "Très surévalué",
      };
      const en = {
        veryUndervalued: "Deep undervalued",
        undervalued: "Undervalued",
        fair: "Fair value",
        overvalued: "Overvalued",
        veryOvervalued: "Deep overvalued",
      };
      return (language === "en" ? en : fr)[bucket];
    };

    const classifyFromMispricing = (mispricingPct) => {
      // mispricing < 0 => undervalued, > 0 => overvalued
      const ratio = Math.max(0.03, Math.min(0.97, 0.5 + (mispricingPct / 260)));
      if (mispricingPct <= -35) return { ratio, tone: "pos", label: labelFor("veryUndervalued"), bucket: "veryUndervalued" };
      if (mispricingPct <= -8) return { ratio, tone: "pos", label: labelFor("undervalued"), bucket: "undervalued" };
      if (mispricingPct < 8) return { ratio, tone: "warn", label: labelFor("fair"), bucket: "fair" };
      if (mispricingPct < 35) return { ratio, tone: "neg", label: labelFor("overvalued"), bucket: "overvalued" };
      return { ratio, tone: "neg", label: labelFor("veryOvervalued"), bucket: "veryOvervalued" };
    };

    // Best source: MOS directly from backend.
    if (Number.isFinite(mos)) {
      // MOS > 0 means undervalued => negative mispricing
      return classifyFromMispricing(-mos);
    }

    // If intrinsic value is non-positive while market price is positive,
    // force far-right overvaluation.
    if (Number.isFinite(dcf) && Number.isFinite(price) && dcf <= 0 && price > 0) {
      return { ratio: 0.97, tone: "neg", label: labelFor("veryOvervalued"), bucket: "veryOvervalued" };
    }

    if (Number.isFinite(dcf) && Number.isFinite(price) && dcf !== 0) {
      const mispricing = ((price - dcf) / Math.abs(dcf)) * 100;
      return classifyFromMispricing(mispricing);
    }

    // Last-resort fallback from backend label text with nuanced placement.
    if (consensus.includes("under")) return { ratio: 0.28, tone: "pos", label: labelFor("undervalued"), bucket: "undervalued" };
    if (consensus.includes("over") || consensus.includes("sur")) return { ratio: 0.72, tone: "neg", label: labelFor("overvalued"), bucket: "overvalued" };
    return { ratio: 0.5, tone: "warn", label: labelFor("fair"), bucket: "fair" };
  }, [data, language]);
  const upsidePct = useMemo(() => {
    const u = Number(data?.valuation?.upsideBeta);
    if (Number.isFinite(u)) return u;
    const mos = Number(data?.valuation?.mosPct);
    if (Number.isFinite(mos)) return mos;
    const dcf = Number(data?.valuation?.dcfValue);
    const price = Number(data?.valuation?.currentPrice || data?.price);
    if (!Number.isFinite(dcf) || !Number.isFinite(price) || price === 0 || dcf <= 0) return null;
    return ((dcf - price) / price) * 100;
  }, [data]);

  const months = useMemo(() => {
    const fr = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jui", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];
    const en = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return language === "en" ? en : fr;
  }, [language]);

  const distributionUi = useMemo(() => {
    const d = data?.distribution || {};
    const payments = Array.isArray(d.dividendPayments) ? d.dividendPayments : [];
    const annual = Number(d.annualDividendPerShare);
    const hasAnnual = Number.isFinite(annual) && annual > 0;
    const byMonth = Array.from({ length: 12 }, () => ({ count: 0, total: 0 }));
    const pmtByMonth = {};
    payments.forEach((p) => {
      const mo = Number(p.month);
      if (!Number.isFinite(mo) || mo < 1 || mo > 12) return;
      const idx = mo - 1;
      byMonth[idx].count += 1;
      byMonth[idx].total += Number(p.amountEur) || 0;
      if (p.paymentDate) pmtByMonth[idx] = String(p.paymentDate);
    });
    const paidCount = byMonth.filter((x) => x.count > 0).length;
    const dyRaw = d.dividendYield;
    let yieldPct = null;
    if (dyRaw != null && Number.isFinite(Number(dyRaw))) {
      const raw = Number(dyRaw);
      yieldPct = Math.abs(raw) <= 1 ? raw * 100 : raw;
    }
    const pr = d.payoutRatio;
    const prX = d.payoutRatioX;
    const fcf = d.fcfCoverRatio;
    const g5 = d.dividendGrowth5Y;
    const score = d.dividendScore;
    const quality = d.dividendQuality || "—";
    const freqLabel = d.frequencyLabel;
    const inferredFreq =
      freqLabel ||
      (paidCount >= 10
        ? language === "en"
          ? "Monthly"
          : "Mensuel"
        : paidCount >= 3
          ? language === "en"
            ? "Quarterly"
            : "Trimestriel"
          : paidCount === 2
            ? language === "en"
              ? "Semi-annual"
              : "Semestriel"
            : paidCount === 1
              ? language === "en"
                ? "Annual"
                : "Annuel"
              : "—");
    const barData = months.map((lbl, idx) => ({
      label: lbl,
      m: idx,
      amt: byMonth[idx].total,
      paid: byMonth[idx].count > 0,
    }));
    const maxBar = Math.max(...barData.map((b) => Number(b.amt) || 0), 1e-9);
    const peakIdx = barData.reduce((best, b, i, arr) => (Number(b.amt) > Number(arr[best]?.amt || 0) ? i : best), 0);
    const peakPaid = barData[peakIdx]?.paid;
    const nbYears = d.nbYearsRegularPayment;
    const regularityStr =
      nbYears != null && Number.isFinite(Number(nbYears))
        ? `${Math.round(Number(nbYears))} ${language === "en" ? "yrs" : "ans"}`
        : "—";
    const summaryText =
      d.summary ||
      d.distributionNarrative ||
      (hasAnnual && yieldPct != null
        ? language === "en"
          ? `${inferredFreq} dividend of ${fmtNum(annual, 2)} EUR (yield ${fmtNum(yieldPct, 2)}%). Payout ${formatDistPayout(pr, prX)}, FCF cover ${formatDistFcf(fcf)}. Quality: ${String(quality).toLowerCase()}.`
          : `Versement ${String(inferredFreq).toLowerCase()} de ${fmtNum(annual, 2)} EUR (yield ${fmtNum(yieldPct, 2)} %). Payout ${formatDistPayout(pr, prX)}, couverture FCF ${formatDistFcf(fcf)}. Qualité : ${String(quality).toLowerCase()}.`
        : language === "en"
          ? "Assess dividend sustainability using cash cover and payout vs earnings."
          : "Évaluer la soutenabilité du dividende via couverture cash et payout.");
    return {
      hasAnnual,
      annual,
      currency: d.currency || c.currency || data?.valuation?.currency || "EUR",
      byMonth,
      pmtByMonth,
      paidCount,
      barData,
      maxBar,
      peakIdx,
      peakPaid,
      yieldFrac: dyRaw,
      yieldPct,
      yieldDisplay: yieldPct != null ? `${fmtNum(yieldPct, 2)}%` : "—",
      payoutDisplay: formatDistPayout(pr, prX),
      fcfDisplay: formatDistFcf(fcf),
      growthDisplay: formatDistGrowth5y(g5),
      regularityStr,
      inferredFreq,
      qualityLabel: quality,
      scoreRounded: Number.isFinite(Number(score)) ? Math.round(Number(score)) : "—",
      scoreTone: distScoreTone(score),
      payoutCol: payoutColor(pr, prX),
      fcfCol: fcfCoverColor(fcf),
      yieldCol: yieldColor(dyRaw),
      growthCol:
        g5 == null || !Number.isFinite(Number(g5))
          ? "var(--muted)"
          : Math.abs(Number(g5)) < 1e-12
            ? C.amber
            : C.green,
      regCol: nbYears != null && Number(nbYears) >= 5 ? C.green : C.amber,
      summaryText,
    };
  }, [data, language, months]);
  const ftiles = useMemo(() => {
    const f = data?.fundamentalTiles || {};
    return [
      { key: "profitabilite", label: language === "en" ? "Profitability" : "Profitabilité", n: Number(f?.profitabilite?.n), n1: Number(f?.profitabilite?.n1), tip: f?.profitabilite?.tooltip || null },
      { key: "rentabilite", label: language === "en" ? "Rentability" : "Rentabilité", n: Number(f?.rentabilite?.n), n1: Number(f?.rentabilite?.n1), tip: f?.rentabilite?.tooltip || null },
      { key: "croissance", label: language === "en" ? "Growth" : "Croissance", n: Number(f?.croissance?.n), n1: Number(f?.croissance?.n1), tip: f?.croissance?.tooltip || null },
      { key: "cashFlow", label: "Cash Flow", n: Number(f?.cashFlow?.n), n1: Number(f?.cashFlow?.n1), tip: f?.cashFlow?.tooltip || null },
      { key: "efficacite", label: language === "en" ? "Efficiency" : "Efficacité", n: Number(f?.efficacite?.n), n1: Number(f?.efficacite?.n1), tip: f?.efficacite?.tooltip || null },
      { key: "allocationCapital", label: language === "en" ? "Capital Alloc." : "Alloc. Capital", n: Number(f?.allocationCapital?.n), n1: Number(f?.allocationCapital?.n1), tip: f?.allocationCapital?.tooltip || null },
      { key: "solvabilite", label: language === "en" ? "Solvency" : "Solvabilité", n: Number(f?.solvabilite?.n), n1: Number(f?.solvabilite?.n1), tip: f?.solvabilite?.tooltip || null },
      { key: "liquidite", label: language === "en" ? "Liquidity" : "Liquidité", n: Number(f?.liquidite?.n), n1: Number(f?.liquidite?.n1), tip: f?.liquidite?.tooltip || null },
      { key: "valueCreation", label: language === "en" ? "Value creation" : "Création de valeur", n: Number(f?.valueCreation?.n), n1: Number(f?.valueCreation?.n1), tip: f?.valueCreation?.tooltip || null },
    ].map((x) => ({
      ...x,
      n: Number.isFinite(x.n) ? x.n : null,
      n1: Number.isFinite(x.n1) ? x.n1 : null,
    }));
  }, [data, language]);
  const fscoreValue = useMemo(() => {
    const v = Number(data?.fundamentalTiles?.fscore);
    if (Number.isFinite(v)) return v;
    return normalizeScore(data?.iScore);
  }, [data]);
  const fForces = useMemo(() => ftiles.filter((x) => Number.isFinite(x.n)).sort((a, b) => b.n - a.n).slice(0, 3), [ftiles]);
  const fVigilances = useMemo(() => ftiles.filter((x) => Number.isFinite(x.n) && x.n < 60).sort((a, b) => a.n - b.n).slice(0, 2), [ftiles]);
  const fYears = useMemo(() => {
    const yf = Number(data?.fundamentalTiles?.yearFrom);
    const yn = Number(data?.fundamentalTiles?.yearN);
    if (Number.isFinite(yf) && Number.isFinite(yn) && yf <= yn) {
      return Array.from({ length: yn - yf + 1 }, (_, i) => yf + i);
    }
    if (!Number.isFinite(yn)) return [2020, 2021, 2022, 2023, 2024];
    return [yn - 4, yn - 3, yn - 2, yn - 1, yn];
  }, [data]);

  const hasGauge = Number.isFinite(Number(data?.valuation?.dcfValue)) && Number.isFinite(Number(data?.valuation?.currentPrice || data?.price));
  const hasHHI = Number.isFinite(Number(data?.marketStructure?.hhiScore));
  const hasRiskPoint = Number.isFinite(Number(data?.riskReturn?.cagrWeighted)) && Number.isFinite(Number(data?.riskReturn?.maxDrawdown));
  const hasPriceChart = Array.isArray(chartData) && chartData.length > 1;
  const hasBacktestChart = Array.isArray(backtestData) && backtestData.length > 1;
  const cagrTrColor = (() => {
    const tr = backtestMetaResolved?.cagrTr;
    const bench = backtestMetaResolved?.cagrBenchmark;
    if (tr == null || !Number.isFinite(Number(tr))) return C.orange;
    if (bench != null && Number.isFinite(Number(bench))) {
      return Number(tr) >= Number(bench) ? C.green : C.orange;
    }
    return Number(tr) >= 0 ? C.green : C.orange;
  })();
  const marketUi = useMemo(() => {
    const hhi = Number(data?.marketStructure?.hhiScore);
    const top3 = Number(data?.marketStructure?.top3SharePct ?? data?.marketStructure?.revenueShare);
    const hhi1y = Number(data?.marketStructure?.hhiTrend1yPts);
    const roic3y = Number(data?.marketStructure?.roicDelta3yPts);
    const roicWeighted = Number(data?.marketStructure?.roicWeighted);
    const winners = Number(data?.marketStructure?.shareWinnersPct);
    const conc = data?.marketStructure?.marketHhiLabel || data?.marketStructure?.marketConcentrationLabel || (hhi < 1500 ? "Fragmenté" : hhi < 2500 ? "Modéré" : "Concentré");
    const concTone = toneFromTag(data?.marketStructure?.concentrationTone, hhi < 1500 ? "pos" : hhi < 2500 ? "warn" : "neg");
    const phase = data?.marketStructure?.marketPhase || (language === "en" ? "Balanced competition" : "Compétition équilibrée");
    const phaseTone = toneFromTag(data?.marketStructure?.marketPhaseTone, concTone);
    const top3Label = Number.isFinite(top3) ? fmtPct(top3) : (data?.marketStructure?.competitiveStructure || "—");
    const hhiTrendLabel = Number.isFinite(hhi1y)
      ? fmtPts(hhi1y)
      : (data?.marketStructure?.marketRelevance || data?.marketStructure?.marketFlow || "—");
    const roicLabel = Number.isFinite(roic3y)
      ? fmtPts(roic3y * 100)
      : (data?.marketStructure?.economicQuality || "—");
    const roicWeightedLabel = Number.isFinite(roicWeighted) ? fmtPct(roicWeighted) : "—";
    const winnersLabel = Number.isFinite(winners)
      ? fmtPct(winners)
      : (data?.marketStructure?.marketFlow || data?.marketStructure?.competitiveStructure || "—");
    const hhiArrow = arrowFromTag(data?.marketStructure?.hhiArrow);
    const roicArrow = arrowFromTag(data?.marketStructure?.roicArrow);
    const cluster = normalizeClusterLabel(data?.marketStructure?.cluster);
    return { hhi, top3, hhi1y, roic3y, roicWeighted, winners, conc, concTone, phase, phaseTone, top3Label, hhiTrendLabel, roicLabel, roicWeightedLabel, winnersLabel, hhiArrow, roicArrow, cluster };
  }, [data, language]);
  const timingUi = useMemo(() => {
    const arr = chartData.filter((x) => Number.isFinite(x.close));
    if (!arr.length) {
      return {
        startLabel: "—",
        endLabel: "—",
        lastClose: null,
        periodPct: null,
        trendTone: "neutral",
        trendLabel: data?.timing?.trendLabel || "—",
        momentumLabel: "—",
        momentumTone: "neutral",
        structureLabel: "—",
        structureTone: "neutral",
        volumeLabel: data?.timing?.timingSignalLabel || data?.timing?.timingSignal || "—",
        volumeTone: "warn",
        sma20: null,
        sma50: null,
        sma200: null,
      };
    }
    const first = arr[0];
    const last = arr[arr.length - 1];
    const startLabel = first.date ? first.date.slice(0, 7).replace("-", ".") : "—";
    const endLabel = last.date ? last.date.slice(0, 7).replace("-", ".") : "—";
    const periodPct = first.close ? ((last.close - first.close) / Math.abs(first.close)) * 100 : null;
    const sma20 = Number.isFinite(last.sma20) ? last.sma20 : null;
    const sma50 = Number.isFinite(last.sma50) ? last.sma50 : null;
    const sma200 = Number.isFinite(last.sma200) ? last.sma200 : null;
    const trendTone = periodPct == null ? "neutral" : periodPct >= 0 ? "pos" : "neg";
    const trendLabel = data?.timing?.trendLabel || (periodPct != null && periodPct >= 0 ? "Haussier" : "Baissier");
    const momentumTone = sma20 != null ? (last.close >= sma20 ? "pos" : "neg") : "warn";
    const momentumLabel = sma20 == null ? "—" : last.close >= sma20 ? "Haussier" : "Baissier";
    const structureTone = sma200 != null ? (last.close >= sma200 ? "pos" : "neg") : "warn";
    const structureLabel = sma200 == null ? "—" : last.close >= sma200 ? "Au-dessus MMA 200" : "Sous la MMA 200";
    const vols = arr.map((x) => x.vol).filter((v) => Number.isFinite(v));
    const vRecent = vols.slice(-10);
    const vPrev = vols.slice(-30, -10);
    const avg = (a) => (a.length ? a.reduce((s, n) => s + n, 0) / a.length : null);
    const vr = avg(vRecent);
    const vp = avg(vPrev);
    const apiVolumeLabel = String(data?.timing?.timingSignalLabel || data?.timing?.timingSignal || "").trim();
    let volumeLabel = apiVolumeLabel || "Neutre";
    let volumeTone = "warn";
    if (apiVolumeLabel) {
      const s = apiVolumeLabel.toLowerCase();
      if (s.includes("accum") || s.includes("fort") || s.includes("haus")) volumeTone = "pos";
      else if (s.includes("distrib") || s.includes("faible") || s.includes("bais")) volumeTone = "neg";
      else volumeTone = "warn";
    } else if (vr != null && vp != null && vp > 0 && periodPct != null) {
      const ratio = vr / vp;
      if (ratio > 1.15 && periodPct < 0) {
        volumeLabel = "Distribution";
        volumeTone = "neg";
      } else if (ratio > 1.15 && periodPct > 0) {
        volumeLabel = "Accumulation";
        volumeTone = "pos";
      } else if (ratio < 0.9) {
        volumeLabel = "Volume faible";
        volumeTone = "warn";
      } else {
        volumeLabel = "Neutre";
        volumeTone = "warn";
      }
    }
    return {
      startLabel,
      endLabel,
      lastClose: last.close,
      periodPct,
      trendTone,
      trendLabel,
      momentumLabel,
      momentumTone,
      structureLabel,
      structureTone,
      volumeLabel,
      volumeTone,
      sma20,
      sma50,
      sma200,
    };
  }, [chartData, data]);
  const riskUi = useMemo(() => {
    const cagr = Number(data?.riskReturn?.cagrWeighted);
    const dd = Number(data?.riskReturn?.maxDrawdown);
    const sharpe = Number(data?.riskReturn?.sharpe);
    const profileLabel = Number.isFinite(dd)
      ? (Math.abs(dd) < 20 ? (language === "en" ? "Low risk" : "Risque faible")
        : Math.abs(dd) < 35 ? (language === "en" ? "Moderate risk" : "Risque modéré")
          : (language === "en" ? "High risk" : "Risque élevé"))
      : (language === "en" ? "Risk profile" : "Profil risque");
    const profileTone = Number.isFinite(dd) ? (Math.abs(dd) < 20 ? "pos" : Math.abs(dd) < 35 ? "warn" : "neg") : "warn";
    const perfLabel = Number.isFinite(cagr)
      ? (cagr >= 8 ? (language === "en" ? "Strong return" : "Rendement fort")
        : cagr >= 3 ? (language === "en" ? "Moderate return" : "Rendement moyen")
          : (language === "en" ? "Weak return" : "Très faible rendement"))
      : (language === "en" ? "Return profile" : "Rendement");
    const perfTone = Number.isFinite(cagr) ? (cagr >= 8 ? "pos" : cagr >= 3 ? "warn" : "neg") : "warn";
    const badgeTone = (profileTone === "neg" || perfTone === "neg") ? "neg" : (profileTone === "warn" || perfTone === "warn") ? "warn" : "pos";
    const badgeLabel = data?.riskReturn?.profileShort
      || data?.riskReturn?.quadrant
      || (badgeTone === "pos" ? (language === "en" ? "Balanced" : "Équilibré") : badgeTone === "warn" ? (language === "en" ? "Mixed" : "Mitigé") : (language === "en" ? "Unbalanced" : "Déséquilibré"));
    return { cagr, dd, sharpe, profileLabel, profileTone, perfLabel, perfTone, badgeTone, badgeLabel };
  }, [data, language]);

  const financialUi = useMemo(() => {
    const cap = data?.capital || {};
    const cfoRaw = cap.cfoAmount ?? cap.freeCashFlow;
    const cfoM = toDisplayMillions(cfoRaw);
    const absM = (raw) => {
      const m = toDisplayMillions(raw);
      return m != null ? Math.abs(m) : 0;
    };
    const bfrM = absM(cap.deltaBfrAmount);
    const capexM = absM(cap.capexAmount);
    const debtM = absM(cap.debtAmount);
    const eqM = absM(cap.shareholdersAmount);
    let needsM = toDisplayMillions(cap.needsAmount);
    const partsSum = bfrM + capexM + debtM + eqM;
    if ((needsM == null || needsM <= 0) && partsSum > 0) needsM = partsSum;

    let coveragePct = cap.coveragePct != null ? Number(cap.coveragePct) : null;
    if ((coveragePct == null || !Number.isFinite(coveragePct)) && cfoM != null && needsM != null && needsM > 0) {
      coveragePct = (cfoM / needsM) * 100;
    }
    if (coveragePct != null && Number.isFinite(coveragePct)) coveragePct = Math.round(coveragePct * 10) / 10;

    const totalSeg = bfrM + capexM + debtM + eqM;
    /** Curseur aligné sur le % de couverture (CFO / besoins), comme la ref — pas sur la somme des segments seule. */
    const pointerLinearPct =
      coveragePct != null && Number.isFinite(coveragePct) && coveragePct >= 0
        ? Math.min(100, coveragePct)
        : 0;
    const pointerBarColor =
      coveragePct == null || !Number.isFinite(coveragePct)
        ? "rgba(255,255,255,.94)"
        : coveragePct < 0
          ? C.red
          : coveragePct >= 100
            ? C.green
            : coveragePct >= 50
              ? C.amber
              : C.red;
    const pointerLabelColor =
      coveragePct == null || !Number.isFinite(coveragePct)
        ? "var(--text-strong)"
        : coveragePct < 0
          ? C.red
          : coveragePct >= 100
            ? C.green
            : coveragePct >= 50
              ? C.amber
              : C.red;

    const multFor = (needM) => {
      if (needM == null || cfoM == null) return null;
      if (needM <= 0) return 0;
      return cfoM / needM;
    };
    const bfrMult = multFor(bfrM || null);
    const capexMult = multFor(capexM || null);
    const debtMult = multFor(debtM || null);
    const eqMult = multFor(eqM || null);

    const stBfr = efStatusFromMult(bfrMult, bfrM);
    const stCapex = efStatusFromMult(capexMult, capexM);
    const stDebt = efStatusFromMult(debtMult, debtM);
    const stEq = efStatusFromMult(eqMult, eqM);

    const segments = [
      { id: "bfr", labelKey: "efBfr", amount: bfrM, raw: cap.deltaBfrAmount, color: SEG_COLORS_EF[0] },
      { id: "capex", labelKey: "efCapex", amount: capexM, raw: cap.capexAmount, color: SEG_COLORS_EF[1] },
      { id: "debt", labelKey: "efDebt", amount: debtM, raw: cap.debtAmount, color: SEG_COLORS_EF[2] },
      { id: "equity", labelKey: "efShareholders", amount: eqM, raw: cap.shareholdersAmount, color: SEG_COLORS_EF[3] },
    ];

    const badgeFromApi = cap.balanceLabel;
    const badgeTone = cap.coverageTone ? toneFromTag(cap.coverageTone, statusTone(badgeFromApi)) : (badgeFromApi ? statusTone(badgeFromApi) : efBadgeToneFromPct(coveragePct));

    const pctColor =
      coveragePct == null ? "var(--muted)" : coveragePct >= 100 ? C.green : coveragePct >= 50 ? C.amber : C.red;

    const hasMeter = totalSeg > 0;

    return {
      cap,
      cfoM,
      needsM,
      coveragePct,
      pctColor,
      badgeFromApi,
      badgeTone,
      segments,
      pointerLinearPct,
      pointerBarColor,
      pointerLabelColor,
      hasMeter,
      rows: [
        { id: "bfr", labelKey: "efBfr", amount: cap.deltaBfrAmount, mult: bfrMult, status: stBfr, tip: cap.deltaBfrTooltip || null },
        { id: "capex", labelKey: "efCapex", amount: cap.capexAmount, mult: capexMult, status: stCapex, tip: cap.capexTooltip || null },
        { id: "debt", labelKey: "efDebt", amount: cap.debtAmount, mult: debtMult, status: stDebt, tip: cap.debtTooltip || null },
        { id: "eq", labelKey: "efShareholders", amount: cap.shareholdersAmount, mult: eqMult, status: stEq, tip: cap.shareholdersTooltip || null },
      ],
    };
  }, [data]);

  const openAnalysis = (sectionKey, narrative) => {
    const formulas = {
      decision: language === "en"
        ? `Score = normalized aggregation of section signals (iScore=${normalizeScore(data?.iScore)}/100).`
        : `Score = agrégation normalisée des signaux (iScore=${normalizeScore(data?.iScore)}/100).`,
      fundamentals: language === "en"
        ? `Radar values come from API radar[] fallback: iScore / valuation score / market score / momentum score / sharpe-derived score.`
        : `Le radar vient de radar[] API, avec fallback: iScore / score valo / score marché / score momentum / score dérivé du sharpe.`,
      valuation: language === "en"
        ? `Gauge ratio = min(max((price / dcf) / 2,0),1). price=${fmtNum(data?.valuation?.currentPrice || data?.price, 4)}, dcf=${fmtNum(data?.valuation?.dcfValue, 4)}.`
        : `Ratio jauge = min(max((prix / dcf) / 2,0),1). prix=${fmtNum(data?.valuation?.currentPrice || data?.price, 4)}, dcf=${fmtNum(data?.valuation?.dcfValue, 4)}.`,
      market: language === "en"
        ? `HHI bar uses hhiScore on 0-10,000 scale. Cursor = hhiScore (${fmtNum(data?.marketStructure?.hhiScore)}).`
        : `La barre IHH utilise hhiScore sur une échelle 0-10 000. Curseur = hhiScore (${fmtNum(data?.marketStructure?.hhiScore)}).`,
      timing: language === "en"
        ? `Chart uses priceChart API series: close + SMA20/SMA50/SMA200 when available.`
        : `Le graphique utilise la série API priceChart: close + SMA20/SMA50/SMA200 quand disponibles.`,
      risk: language === "en"
        ? `Quadrant point = (x=|maxDrawdown|, y=CAGR). x=${fmtNum(Math.abs(Number(data?.riskReturn?.maxDrawdown || 0)))}, y=${fmtNum(data?.riskReturn?.cagrWeighted)}.`
        : `Point du quadrant = (x=|maxDrawdown|, y=CAGR). x=${fmtNum(Math.abs(Number(data?.riskReturn?.maxDrawdown || 0)))}, y=${fmtNum(data?.riskReturn?.cagrWeighted)}.`,
      financial: language === "en"
        ? `CFO coverage % = CFO / total needs. Stacked bar = Δ NWC, CAPEX, debt service, shareholder payouts (width ∝ share of needs). Pointer = CFO as % of total needs.`
        : `Couverture CFO = CFO / besoins totaux. Barre = Δ BFR, CAPEX, dette, actionnaires (largeur ∝ part des besoins). Curseur = position du CFO en % des besoins.`,
      backtest: language === "en"
        ? `Backtest chart uses API series backtest[] (asset_tr/index_tr fallback asset_price/index_price).`
        : `Le graphe backtest utilise la série API backtest[] (asset_tr/index_tr ou fallback asset_price/index_price).`,
      distribution: language === "en"
        ? `distribution.dividendPayments[], annualDividendPerShare, dividendYield, payout, FCF cover from API; bar chart = monthly amounts.`
        : `distribution.dividendPayments[], annualDividendPerShare, dividendYield, payout, couverture FCF via API ; barres = montants mensuels.`,
    };
    setActiveAnalysis({
      sectionKey,
      narrative: narrative || t("noData"),
      formula: formulas[sectionKey] || "",
    });
  };

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") setActiveAnalysis(null);
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  return (
    <div style={{ minHeight: "100vh", padding: "16px 12px 24px", background: "linear-gradient(180deg,var(--app-bg-from),var(--app-bg-to))", overflowX: "hidden" }}>
      <style>{`
        .ticker-shell{width:100%;max-width:1820px;margin:0 auto;box-sizing:border-box;overflow-x:hidden;}
        .hero-main-card{width:100%;max-width:100%;box-sizing:border-box;}
        .ticker-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:16px;align-items:stretch;margin-top:16px;}
        .ticker-grid > *{min-width:0;}
        .section-pad{padding:0 20px 16px;min-width:0;}
        .ticker-grid .section-pad{display:flex;flex-direction:column;height:100%;}
        .mini-grid-2{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;}
        .mini-grid-2-risk{
          display:grid;grid-template-columns:repeat(2,minmax(0,1fr));
          column-gap:10px;row-gap:20px;
          margin-top:48px;margin-bottom:4px;
        }
        .mini-grid-2-risk--no-chart{margin-top:0;}
        .mini-grid-3{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;}
        .fund-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;}
        .fund-tile{border-radius:18px;padding:9px 7px;min-height:102px;display:flex;flex-direction:column;align-items:center;justify-content:center;position:relative;overflow:hidden;}
        .fund-tile-label{font-size:10px;font-weight:700;line-height:1.15;text-align:center;color:rgba(255,255,255,.93);}
        .fund-tile-main{margin-top:2px;font-size:40px;font-weight:800;line-height:1;color:rgba(255,255,255,.95);}
        .fund-tile-sub{margin-top:4px;font-size:13px;color:rgba(255,255,255,.78);}
        .val-three{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;margin-top:8px;}
        .val-mini{border:1px solid var(--sep);background:var(--summary-bg);border-radius:14px;padding:9px 10px;}
        .val-mini-k{font-size:11px;color:var(--muted);font-weight:700;text-transform:uppercase;letter-spacing:.06em;font-family:ui-monospace,monospace;}
        .val-mini-v{margin-top:2px;font-size:16px;font-weight:800;font-family:ui-monospace,monospace;line-height:1;}
        .val-mini-s{margin-top:4px;font-size:11px;color:var(--muted);}
        .ms-wrap{display:grid;grid-template-columns:92px 1fr;gap:14px;align-items:start;margin-top:8px;}
        .ms-metrics{display:grid;gap:6px;}
        .ms-row{padding:7px 0;border-bottom:1px solid var(--sep);}
        .ms-row:last-child{border-bottom:none;}
        .ms-k{font-size:11px;color:var(--muted);font-weight:600;display:inline-flex;align-items:center;gap:4px;}
        .ms-v{margin-top:2px;font-size:20px;font-weight:800;color:var(--text-strong);}
        .ms-arrow{display:inline-block;margin-right:6px;font-size:14px;line-height:1;vertical-align:baseline;}
        .ms-sub{margin-left:6px;font-size:11px;color:var(--muted);}
        .ms-phase{margin-top:10px;border-radius:999px;border:1px solid var(--sep);padding:8px 14px;text-align:center;font-size:13px;font-weight:700;}
        .ms-cluster{margin-top:10px;padding-top:10px;border-top:1px solid var(--sep);font-size:12px;color:var(--muted);font-family:ui-monospace, SFMono-Regular, Menlo, monospace;letter-spacing:.06em;text-transform:uppercase;}
        .timing-head{display:flex;align-items:flex-end;justify-content:space-between;gap:10px;margin-bottom:8px;}
        .timing-price-wrap{display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;min-width:0;margin-top:10px;}
        .timing-price{font-size:36px;font-weight:800;color:var(--text-strong);font-family:ui-monospace,monospace;line-height:1;}
        .timing-var{font-size:22px;font-weight:700;line-height:1.1;}
        .timing-period-label{font-size:12px;color:var(--muted);}
        .timing-range{font-size:16px;color:var(--muted);font-family:ui-monospace,monospace;line-height:1.15;text-align:right;}
        .timing-chart{height:218px;}
        .timing-legend{display:flex;flex-wrap:wrap;gap:12px;margin-top:8px;}
        .timing-legend-item{display:inline-flex;align-items:center;gap:5px;color:var(--muted);font-family:ui-monospace,monospace;font-size:13px;}
        .timing-legend-line{width:20px;height:3px;border-radius:2px;display:inline-block;}
        .ef-section{padding-top:2px;}
        .ef-hero{display:flex;flex-wrap:wrap;align-items:baseline;justify-content:space-between;gap:10px 20px;margin-bottom:16px;width:100%;}
        .ef-hero-left{display:flex;align-items:baseline;flex-wrap:wrap;gap:4px;min-width:0;flex:1 1 auto;}
        .ef-hero-right{display:flex;align-items:baseline;flex-wrap:wrap;gap:2px;min-width:0;justify-content:flex-end;text-align:right;flex:0 1 auto;}
        .ef-hero-cfo-val{font-size:22px;font-weight:800;font-family:ui-monospace,monospace;color:var(--text-strong);letter-spacing:-0.02em;}
        .ef-hero-cfo-unit{font-size:12px;color:var(--muted);font-weight:600;}
        .ef-hero-slash{font-size:15px;color:var(--muted);margin:0 4px 0 0;}
        .ef-hero-needs-val{font-family:ui-monospace,monospace;font-size:13px;font-weight:700;color:var(--muted);}
        .ef-hero-needs-lbl{font-size:12px;color:var(--muted);font-weight:600;}
        .ef-meter-root{margin-top:2px;margin-bottom:12px;}
        .ef-meter-axis{display:flex;justify-content:space-between;margin-bottom:6px;}
        .ef-meter-axis-l{font-size:10px;font-weight:700;color:${C.green};}
        .ef-meter-axis-r{font-size:10px;font-weight:700;color:${C.red};}
        .ef-meter-track-wrap{position:relative;height:38px;}
        .ef-meter-track{position:absolute;inset:0;display:flex;border-radius:14px;overflow:hidden;}
        .ef-meter-seg{position:relative;display:flex;align-items:center;justify-content:center;min-width:0;overflow:hidden;}
        .ef-meter-seg-inner{display:flex;flex-direction:column;align-items:center;justify-content:center;line-height:1;z-index:1;padding:0 2px;}
        .ef-meter-seg-lbl{font-size:10px;font-weight:700;color:rgba(255,255,255,.95);letter-spacing:.04em;text-align:center;}
        .ef-meter-seg-val{font-size:9px;color:rgba(255,255,255,.74);font-family:ui-monospace,monospace;margin-top:1px;}
        .ef-meter-seg-shine{position:absolute;inset:0;pointer-events:none;background:linear-gradient(to bottom,rgba(255,255,255,0.14) 0%,rgba(255,255,255,0) 60%);}
        .ef-meter-cursor{position:absolute;top:0;bottom:0;width:0;transform:translateX(-50%);z-index:10;display:flex;flex-direction:column;align-items:center;pointer-events:none;}
        .ef-meter-cursor-bar{width:2px;height:100%;background:rgba(255,255,255,.95);border-radius:2px;box-shadow:0 0 4px rgba(0,0,0,.28);}
        .ef-meter-pointer-wrap{position:relative;height:20px;margin-top:2px;}
        .ef-meter-pointer-lbl{position:absolute;top:0;transform:translateX(-50%);font-size:10px;font-weight:700;font-family:ui-monospace,monospace;white-space:nowrap;background:var(--card-bg);padding:2px 6px;border-radius:6px;}
        .ef-meter-ticks-nl{position:relative;height:16px;margin-top:6px;margin-bottom:2px;font-family:ui-monospace,monospace;font-size:9px;color:var(--muted);}
        .ef-meter-tick-nl{position:absolute;transform:translateX(-50%);white-space:nowrap;}
        .ef-sep{height:1px;background:var(--sep);margin:14px 0;}
        .ef-sep-tight{margin:8px 0;}
        .ef-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:10px 16px;}
        .ef-cell{padding:2px 0 10px;}
        .ef-cell-top{display:flex;align-items:flex-start;justify-content:space-between;gap:8px;margin-bottom:6px;}
        .ef-cell-lbl{font-size:11.5px;font-weight:700;color:var(--text-strong);display:inline-flex;align-items:center;}
        .ef-cell-amt{font-size:10px;color:var(--muted);font-weight:600;white-space:nowrap;}
        .ef-cell-bot{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
        .ef-cell-mult{font-size:12px;font-weight:800;font-family:ui-monospace,monospace;}
        .ef-cell-arrow{font-size:7px;vertical-align:middle;margin-right:2px;}
        .ef-cell-badge{display:inline-flex;border-radius:999px;padding:3px 10px;font-size:9.5px;font-weight:800;}
        .fscore-box{
          margin-top:10px;border-radius:14px;padding:10px 14px;border:1px solid var(--sep);background:var(--summary-bg);
          display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:12px;
        }
        .fscore-label{
          color:var(--muted);font-size:14px;font-weight:700;line-height:1.12;
        }
        .fscore-value{
          color:${C.green};font-size:24px;font-weight:800;line-height:1;
          font-family:ui-monospace,monospace;white-space:nowrap;letter-spacing:-0.02em;
        }
        .decision-stack{display:grid;gap:6px;min-width:0;}
        .decision-card{margin-top:0;}
        .decision-counts{margin-top:18px;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;}
        .decision-count-card{
          border-radius:12px;border:1px solid var(--sep);background:var(--summary-bg);
          padding:8px 10px;min-height:58px;display:flex;flex-direction:column;justify-content:center;
          min-width:0;overflow:hidden;
        }
        .decision-count-val{
          font-size:36px;font-weight:800;line-height:1;text-align:center;
          font-family:ui-monospace,monospace;
        }
        .decision-count-label{
          color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:.08em;font-weight:700;
          text-align:center;font-family:ui-monospace,monospace;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
        }
        .dist-section{padding-top:4px;}
        .dist-hero-row{
          display:flex;align-items:flex-end;justify-content:space-between;gap:12px;flex-wrap:wrap;
          margin-bottom:clamp(10px,2.5vw,14px);
        }
        .dist-hero-amt{font-size:clamp(20px,5vw,26px);letter-spacing:-0.03em;}
        .dist-hero-unit{font-size:clamp(11px,2.8vw,13px);}
        .dist-cal-grid{
          display:grid;grid-template-columns:repeat(3,minmax(0,1fr));
          gap:clamp(6px,1.6vw,10px);margin-bottom:8px;
        }
        .dist-bar-wrap{
          width:100%;
          height:clamp(118px,26vw,172px);
          margin:4px 0 14px;
        }
        .dist-metrics-grid{
          display:grid;
          grid-template-columns:repeat(2,minmax(0,1fr));
          gap:10px;
          margin-top:4px;
        }
        @media (min-width:900px){
          .dist-metrics-grid{grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;}
          .dist-cal-grid{gap:10px;}
          .dist-bar-wrap{height:clamp(140px,16vw,180px);}
        }
        @media (max-width:480px){
          .dist-hero-row{align-items:flex-start;flex-direction:column;}
          .dist-cal-grid{gap:6px;}
          .dist-bar-wrap{height:112px;margin-bottom:10px;}
        }
        .section-kicker-title{color:var(--muted);font-size:11px;font-weight:800;letter-spacing:.14em;text-transform:uppercase;margin-bottom:8px;}
        .top-meta{display:flex;gap:8px;flex-wrap:wrap;margin:0 0 12px;margin-top:10px;}
        .hero-main{
          display:grid;
          grid-template-columns:minmax(0,0.95fr) minmax(0,1.1fr) minmax(240px,0.85fr);
          gap:clamp(12px,2vw,20px);
          align-items:start;
        }
        .hero-col-profile{min-width:0;}
        .hero-col-desc{
          min-width:0;
          border-left:1px solid var(--sep);
          padding-left:clamp(12px,1.8vw,18px);
        }
        .hero-col-viz{
          min-width:0;
          border-left:1px solid var(--sep);
          padding-left:clamp(12px,1.8vw,18px);
        }
        .hero-desc{
          color:var(--text);
          font-size:12px;
          line-height:1.45;
          margin:0;
          display:-webkit-box;
          -webkit-box-orient:vertical;
          -webkit-line-clamp:3;
          overflow:hidden;
        }
        .hero-desc.expanded{-webkit-line-clamp:unset;display:block;}
        .hero-desc-btn{
          margin-top:8px;border:1px solid var(--hero-desc-border);background:transparent;color:var(--hero-desc-fg);
          border-radius:12px;padding:6px 12px;font-size:12px;font-weight:700;cursor:pointer;
        }
        .hero-card-head{position:relative;padding:16px 20px 0;}
        .hero-card-head-row{padding-right:46px;}
        .theme-fab{
          position:absolute;top:12px;right:14px;z-index:2;width:40px;height:40px;border-radius:12px;
          border:1px solid var(--card-border);background:var(--summary-bg);color:var(--text-strong);
          display:inline-flex;align-items:center;justify-content:center;cursor:pointer;padding:0;
          box-shadow:0 1px 2px rgba(15,23,42,.06);
        }
        .theme-fab:hover{background:var(--badge-bg);}
        .hero-iscore-label{display:inline-flex;align-items:center;gap:6px;}
        .hero-graph-wrap{
          margin-top:0;
          border-top:none;
          padding-top:0;
          display:grid;
          grid-template-columns:160px 1fr;
          gap:12px;
          align-items:center;
        }
        .hero-score-kicker{
          color:var(--muted);
          font-size:11px;
          font-weight:700;
          text-transform:uppercase;
          letter-spacing:.12em;
          font-family:ui-monospace, SFMono-Regular, Menlo, monospace;
          margin-bottom:6px;
        }
        .hero-score-value{
          font-family:ui-monospace, SFMono-Regular, Menlo, monospace;
          font-weight:800;
          font-size:30px;
          line-height:1;
          color:#8ea67a;
        }
        .hero-score-den{font-size:22px;color:var(--muted);margin-left:4px;}
        .ticker-topbar{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-bottom:10px;}
        .back-btn{border:1px solid var(--card-border);border-radius:12px;background:transparent;padding:8px 12px;font-size:13px;color:var(--text);font-weight:700;cursor:pointer}
        .top-hero-gap{margin-bottom:14px;}
        .analysis-modal-overlay{
          position:fixed;inset:0;z-index:1200;
          background:rgba(15,23,42,.42);
          display:flex;align-items:center;justify-content:center;padding:16px;
        }
        .analysis-modal{
          width:min(760px,100%);max-height:85vh;overflow:auto;
          border-radius:16px;background:var(--card-bg);border:1px solid var(--card-border);
          box-shadow:0 24px 60px rgba(0,0,0,.22);
        }
        .analysis-modal-body{padding:12px 20px 20px;}
        .analysis-modal-close{
          border:1px solid var(--card-border);background:transparent;color:var(--text);
          border-radius:10px;padding:6px 10px;font-size:12px;font-weight:700;cursor:pointer;
        }
        .analysis-modal-close-x{
          width:32px;height:32px;border-radius:999px;border:1px solid rgba(198,82,63,.45);
          background:rgba(198,82,63,.12);color:${C.red};display:inline-flex;align-items:center;justify-content:center;
          font-size:20px;line-height:1;font-weight:800;cursor:pointer;padding:0;transition:all .18s ease;
        }
        .analysis-modal-close-x:hover{
          background:rgba(198,82,63,.2);border-color:rgba(198,82,63,.62);
        }
        .fund-tip-drawer-overlay{
          position:fixed;inset:0;z-index:1300;background:rgba(9,15,29,.38);
          display:flex;align-items:center;justify-content:center;padding:14px;
        }
        .fund-tip-drawer{
          width:min(820px, calc(100vw - 28px));max-height:min(82vh,680px);overflow-y:auto;border-radius:16px;
          background:var(--card-bg);border:1px solid var(--card-border);
          box-shadow:0 16px 40px rgba(0,0,0,.25);padding:12px 16px 16px;
        }
        .fund-tip-drawer-title{
          color:var(--text-strong);font-weight:700;font-size:16px;letter-spacing:-0.01em;
        }
        .fund-tip-drawer-body{
          margin-top:10px;border-radius:12px;border:1px solid var(--sep);background:var(--summary-bg);
          padding:12px 14px;color:var(--text);font-size:15px;line-height:1.5;
        }
        @media (min-width:821px){
          .ef-meter-track-wrap{height:42px;}
          .ef-meter-axis-l,.ef-meter-axis-r{font-size:10.5px;}
          .ef-meter-pointer-lbl{font-size:11px;padding:3px 8px;}
          .ef-meter-ticks-nl{font-size:9.5px;height:18px;}
          .ef-hero-cfo-val{font-size:24px;}
          .ef-hero-needs-val{font-size:14px;}
        }
        @media (max-width:1200px){.ticker-grid{grid-template-columns:repeat(2,minmax(0,1fr));}}
        @media (max-width:1020px){
          .hero-main{grid-template-columns:1fr;gap:14px;}
          .hero-col-desc,.hero-col-viz{border-left:none;padding-left:0;}
          .hero-col-desc{border-top:1px solid var(--sep);padding-top:14px;}
          .hero-col-viz{border-top:1px solid var(--sep);padding-top:14px;}
        }
        @media (max-width:820px){
          .ticker-grid{grid-template-columns:1fr;gap:10px;}
          .section-pad{padding:0 16px 14px;}
          .hero-main-card{width:100%;max-width:100%;}
          .mini-grid-3{grid-template-columns:repeat(2,minmax(0,1fr));}
          .fund-grid{grid-template-columns:repeat(3,minmax(0,1fr));gap:7px;}
          .val-three{grid-template-columns:repeat(3,minmax(0,1fr));gap:7px;}
          .val-mini{padding:8px 8px;}
          .val-mini-k{font-size:10px;}
          .val-mini-v{font-size:14px;}
          .val-mini-s{font-size:10px;}
          .ms-wrap{grid-template-columns:88px 1fr;gap:10px;margin-top:10px;}
          .ms-row{padding:7px 0;}
          .ms-k{font-size:11px;}
          .ms-v{font-size:18px;}
          .ms-sub{font-size:11px;}
          .ms-phase{font-size:12px;padding:7px 12px;}
          .timing-head{align-items:flex-start;gap:8px;flex-direction:column;}
          .timing-price{font-size:34px;}
          .timing-var{font-size:28px;}
          .timing-period-label{font-size:14px;}
          .timing-range{font-size:16px;text-align:left;}
          .timing-chart{height:196px;}
          .timing-legend{gap:10px;}
          .timing-legend-item{font-size:13px;}
          .ef-hero-cfo-val{font-size:20px;}
          .ef-hero-needs-val{font-size:12px;}
          .ef-meter-track-wrap{height:34px;}
          .ef-meter-axis-l,.ef-meter-axis-r{font-size:9px;}
          .ef-meter-seg-lbl{font-size:9.5px;}
          .ef-meter-pointer-lbl{font-size:9px;}
          .ef-cell-lbl{font-size:11px;}
          .ef-cell-amt{font-size:9.5px;}
          .ef-cell-mult{font-size:11px;}
          .fund-tile{min-height:92px;padding:8px 5px;border-radius:16px;}
          .fund-tile-main{font-size:26px;}
          .fund-tile-label{font-size:9px;}
          .fund-tile-sub{font-size:11px;}
          .fscore-box{padding:10px 12px;gap:10px;}
          .fscore-label{font-size:12px;line-height:1.1;}
          .fscore-value{font-size:30px;}
          .top-hero-gap{margin-bottom:20px;}
          .ticker-grid{margin-top:14px;}
          .analysis-modal-body{padding:10px 16px 16px;}
          .fund-tip-drawer-overlay{padding:10px;}
          .fund-tip-drawer{width:min(760px, calc(100vw - 20px));max-height:78vh;border-radius:14px;}
          .hero-desc{font-size:17px;line-height:1.42;-webkit-line-clamp:8;}
          .hero-desc-btn{font-size:16px;padding:8px 14px;border-radius:14px;}
          .hero-graph-wrap{grid-template-columns:1fr;gap:8px;}
          .hero-score-kicker{font-size:15px;}
          .hero-score-value{font-size:32px;}
          .hero-score-den{font-size:20px;}
        }
        @media (max-width:520px){
          .hero-card-head{padding:12px 12px 0;}
          .hero-card-head-row{padding-right:42px;}
          .theme-fab{top:10px;right:10px;width:38px;height:38px;border-radius:11px;}
        }
        @media (max-width:480px){
          .mini-grid-2,.mini-grid-3{grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;}
          .mini-grid-2-risk{column-gap:8px;row-gap:16px;margin-top:16px;}
          .hero-desc{font-size:15px;}
          .fund-grid{gap:6px;}
          .decision-counts{gap:6px;}
          .decision-count-card{padding:7px 6px;min-height:54px;}
          .decision-count-val{font-size:30px;}
          .decision-count-label{font-size:9px;letter-spacing:.05em;}
          .val-three{gap:6px;}
          .val-mini{border-radius:12px;padding:7px 7px;}
          .val-mini-v{font-size:13px;}
          .ms-wrap{grid-template-columns:82px 1fr;gap:9px;}
          .ms-v{font-size:16px;}
          .ms-k{font-size:10px;}
          .timing-price{font-size:30px;}
          .timing-var{font-size:24px;}
          .timing-period-label{font-size:13px;}
          .timing-range{font-size:14px;}
          .timing-chart{height:176px;}
          .timing-legend-item{font-size:12px;}
          .ef-hero{flex-direction:column;align-items:flex-start;}
          .ef-hero-right{justify-content:flex-start;text-align:left;width:100%;}
          .ef-hero-cfo-val{font-size:18px;}
          .ef-hero-needs-val{font-size:11px;}
          .ef-meter-track-wrap{height:30px;}
          .ef-meter-seg-lbl{font-size:8.5px;}
          .ef-meter-seg-val{font-size:8px;}
          .ef-meter-pointer-lbl{font-size:8.5px;padding:0 3px;}
          .ef-meter-ticks-nl{font-size:8px;height:14px;}
          .ef-grid-2{gap:8px 10px;}
          .ef-cell-lbl{font-size:10.5px;}
          .ef-cell-mult{font-size:10.5px;}
          .ef-cell-badge{font-size:9px;padding:2px 8px;}
          .fund-tile{min-height:84px;padding:7px 4px;border-radius:14px;}
          .fund-tile-main{font-size:22px;}
          .fund-tile-label{font-size:8.5px;}
          .fund-tile-sub{font-size:10px;}
          .fscore-label{font-size:11px;}
          .fscore-value{font-size:24px;}
        }
      `}</style>

      <div className="ticker-shell">
        <div className="ticker-topbar">
          <button type="button" className="back-btn" onClick={() => navigate(backPath)}>{backLabel}</button>
        </div>

        <Card className="hero-main-card">
          <HeroCardHead
            title={c.symbol || symbol || c.name || "Ticker"}
            companyName={c.name || null}
            kicker={[c.exchange, c.industry || c.sector].filter(Boolean).join(" · ")}
            priceStr={fmtHeroPrice(data?.price ?? data?.valuation?.currentPrice, c.currency || c.exchange)}
            mtdPct={data?.mtdPct}
            ytdPct={data?.ytdPct}
            badge={<StatusBadge tone={statusTone(data?.fundamental)}>{data?.fundamental || t("inReview")}</StatusBadge>}
            darkMode={darkMode}
            onToggleDark={() => setDarkMode((d) => !d)}
            t={t}
          />
          <div className="section-pad top-hero-gap">
            <div className="top-meta">
              {c.marketCapClass ? <StatusBadge>{c.marketCapClass}</StatusBadge> : null}
              {data?.asOf ? <StatusBadge>{`${t("asOf")} ${data.asOf}`}</StatusBadge> : null}
            </div>
            <div className="hero-main">
              <div className="hero-col-profile">
                <div style={{ marginBottom: 4 }}>
                  <div className="section-kicker-title">{t("profileSection")}</div>
                  <ProfileRow
                    label={t("profRegion")}
                    value={c.region}
                    hint={t("profRegionHint") || "Zone geographique et marche principal de l'emetteur."}
                  />
                  <ProfileRow
                    label={t("profExchange")}
                    value={c.exchange}
                    hint={t("profExchangeHint") || "Marche reglemente sur lequel le titre est cote."}
                  />
                  <ProfileRow
                    label={t("profSector")}
                    value={c.sector}
                    hint={t("profSectorHint") || "Secteur economique (classification large)."}
                  />
                  <ProfileRow
                    label={t("profIndustry")}
                    value={c.industry}
                    hint={t("profIndustryHint") || "Industrie ou sous-secteur detaille."}
                  />
                  <ProfileRow
                    label={t("profCap")}
                    value={c.marketCapClass}
                    hint={t("profCapHint") || "Segment de capitalisation (Large, Mid, Small...)."}
                  />
                  <ProfileRow
                    label={t("profStyle")}
                    value={c.style}
                    hint={t("profStyleHint") || "Style d'investissement (Core, Value, Growth...)."}
                  />
                  <ProfileRow
                    label={t("profCycle")}
                    value={c.cyclicals}
                    hint={t("profCycleHint") || "Sensibilite au cycle economique (cyclique, defensif...)."}
                  />
                </div>
              </div>
              <div className="hero-col-desc">
                {c.description ? (
                  <div>
                    <div className="section-kicker-title">{t("descriptionSection")}</div>
                    <p className={`hero-desc ${expandedDesc ? "expanded" : ""}`}>{c.description}</p>
                    {String(c.description).length > 260 ? (
                      <button type="button" className="hero-desc-btn" onClick={() => setExpandedDesc((v) => !v)}>
                        {expandedDesc ? t("seeLess") : t("seeMore")}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className="hero-col-viz">
                <div className="hero-graph-wrap">
                  <div>
                    <div className="hero-score-kicker hero-iscore-label">
                      {t("iscoreLabel")}
                      <span className="profile-info-ic" title={t("iscoreHint")} style={{ display: "inline-flex", color: "var(--profile-info-muted)" }}>
                        <Info size={14} strokeWidth={2} aria-hidden />
                      </span>
                    </div>
                    <div>
                      <span className="hero-score-value">{normalizeScore(data?.iScore)}</span>
                      <span className="hero-score-den">/100</span>
                    </div>
                  </div>
                  <div style={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={radarData} outerRadius="66%">
                        <PolarGrid stroke="var(--sep)" />
                        <PolarRadiusAxis domain={[0, 100]} tick={{ fill: "var(--muted)", fontSize: 9 }} />
                        <PolarAngleAxis dataKey="axis" tick={{ fill: "var(--muted)", fontSize: 11, fontWeight: 600 }} />
                        <RechartsTooltip
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const row = payload[0]?.payload;
                            if (!row || row.value == null) return null;
                            const v = normalizeScore(row.value);
                            return (
                              <div
                                style={{
                                  background: "var(--card-bg)",
                                  border: "1px solid var(--card-border)",
                                  borderRadius: 10,
                                  padding: "8px 12px",
                                  boxShadow: "0 6px 20px rgba(15,23,42,.12)",
                                }}
                              >
                                <div style={{ fontWeight: 800, color: C.green, fontFamily: "ui-monospace,monospace", fontSize: 15 }}>
                                  {v}/100
                                </div>
                                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{radarQualLabel(v, language)}</div>
                                <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 4, fontWeight: 600 }}>{row.axis}</div>
                              </div>
                            );
                          }}
                        />
                        <RechartsRadar
                          name="Score"
                          dataKey="value"
                          stroke={C.green}
                          fill={C.green}
                          fillOpacity={0.2}
                          strokeWidth={2.2}
                          dot={{ r: 4, fill: C.green, stroke: "rgba(255,255,255,.92)", strokeWidth: 1.5 }}
                          activeDot={{ r: 6 }}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {loading ? <div style={{ padding: 24, textAlign: "center", color: "var(--muted)", fontWeight: 700 }}>{t("loading")}</div> : null}
        {error ? <div style={{ padding: 24, textAlign: "center", color: C.red, fontWeight: 700 }}>{error}</div> : null}

        {!loading && !error && data ? (
          <div className="ticker-grid">
            <Card className="decision-card">
              <CardHead title={t("decision")} right={<StatusBadge tone={decisionTone}>{`${decisionScore}/100`}</StatusBadge>} />
              <div className="section-pad">
                <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                  {decisionItems.map((item, idx) => (
                    <div
                      key={`decision-strip-${idx}`}
                      style={{
                        height: 6,
                        borderRadius: 999,
                        flex: 1,
                        background: item.tone === "pos" ? "#839f6d" : item.tone === "warn" ? "#b7a972" : "#cd8a69",
                      }}
                    />
                  ))}
                </div>
                <div className="decision-stack">
                  {decisionItems.map((item, i) => <DecisionRow key={`${item.label}-${i}`} label={item.label} score={item.score} tone={item.tone} tip={item.tip} />)}
                </div>
                <div className="decision-counts">
                  <div className="decision-count-card">
                    <div className="decision-count-val" style={{ color: C.green }}>{String(yesCount)}</div>
                    <div className="decision-count-label">{t("favorable")}</div>
                  </div>
                  <div className="decision-count-card">
                    <div className="decision-count-val" style={{ color: C.amber }}>{String(watchCount)}</div>
                    <div className="decision-count-label">{t("vigilance")}</div>
                  </div>
                  <div className="decision-count-card">
                    <div className="decision-count-val" style={{ color: C.red }}>{String(noCount)}</div>
                    <div className="decision-count-label">{t("unfavorable")}</div>
                  </div>
                </div>
                <SummaryBox>
                  {`Synthèse : ${data?.decisionSummary || data?.marketStructure?.narrative || data?.timing?.timingNarrative || "Lecture combinée des signaux fondamentaux, de valorisation et de timing pour cadrer la décision."}`}
                </SummaryBox>
                <CTA label={t("seeAnalysis")} onClick={() => openAnalysis("decision", data?.decisionSummary || data?.marketStructure?.narrative || data?.timing?.timingNarrative)} />
              </div>
            </Card>

            <Card>
              <CardHead title={t("fundamentals")} right={<StatusBadge tone={statusTone(data?.fundamentalTiles?.interpretation || data?.fundamental)}>{`${data?.fundamentalTiles?.interpretation || data?.fundamental || "Neutral"} · ${Math.round(fscoreValue)}/100`}</StatusBadge>} />
              <div className="section-pad">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  {fYears.map((y) => (
                    <span key={y} style={{ color: y === fYears[fYears.length - 1] ? "var(--text-strong)" : "var(--muted)", fontSize: 11, fontWeight: y === fYears[fYears.length - 1] ? 700 : 500, fontFamily: "ui-monospace,monospace" }}>
                      {y}
                    </span>
                  ))}
                </div>
                <div className="fund-grid">
                  {ftiles.map((tile) => {
                    const n = Number.isFinite(tile.n) ? tile.n : 50;
                    const n1 = Number.isFinite(tile.n1) ? tile.n1 : n;
                    const base = colorForPct(n);
                    const bg = `linear-gradient(135deg,${shade(base, 0.16)} 0%,${shade(base, -0.08)} 100%)`;
                    const trend = n > n1 ? "↗" : n < n1 ? "↘" : "→";
                    const isAlert = n < 60;
                    const trendColor = trend === "↗" ? "#6ee87a" : trend === "↘" ? "#f87171" : "#fcd34d";
                    return (
                      <button
                        key={tile.key}
                        type="button"
                        className="fund-tile"
                        onClick={() => {
                          if (!tile.tip) return;
                          setActiveFundTip({ label: tile.label, text: tile.tip });
                        }}
                        style={{ background: bg, border: isAlert ? `2px solid ${C.red}` : "1px solid rgba(255,255,255,.2)", cursor: tile.tip ? "pointer" : "default", textAlign: "center" }}
                      >
                        <span style={{ position: "absolute", top: 6, right: 8, borderRadius: 999, padding: "2px 7px", fontSize: 10, fontWeight: 800, background: "rgba(20,25,21,.45)", color: trendColor }}>{trend}</span>
                        <div className="fund-tile-label">{tile.label}</div>
                        <div className="fund-tile-main">{Math.round(n)}%</div>
                        <div className="fund-tile-sub">vs {Math.round(n1)}%</div>
                      </button>
                    );
                  })}
                </div>
                <div className="fscore-box">
                  <span className="fscore-label">
                    {language === "en" ? "FScore global" : "FScore global"}<br />
                    {language === "en" ? `· Avg. ${fYears[0]}–${fYears[fYears.length - 1]}` : `· Moy. ${fYears[0]}–${fYears[fYears.length - 1]}`}
                  </span>
                  <span className="fscore-value">{fmtNum(fscoreValue, 1)}/100</span>
                </div>
                <div className="mini-grid-2" style={{ marginTop: 10 }}>
                  <div style={{ borderRadius: 14, border: `1px solid ${C.green}44`, background: `${C.green}14`, padding: "10px 12px" }}>
                    <div style={{ color: C.green, fontSize: 11, fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 6, fontFamily: "ui-monospace,monospace" }}>{language === "en" ? "Strengths" : "Forces"}</div>
                    {fForces.map((x) => (
                      <div key={`force-${x.key}`} style={{ display: "flex", justifyContent: "space-between", gap: 8, color: "var(--text)", fontSize: 13, marginTop: 3 }}>
                        <span>{x.label}</span>
                        <span style={{ color: C.green, fontWeight: 700, fontFamily: "ui-monospace,monospace" }}>{Math.round(x.n)}%</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ borderRadius: 14, border: `1px solid ${C.red}44`, background: `${C.red}12`, padding: "10px 12px" }}>
                    <div style={{ color: C.red, fontSize: 11, fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 6, fontFamily: "ui-monospace,monospace" }}>{language === "en" ? "Warnings" : "Vigilances"}</div>
                    {(fVigilances.length ? fVigilances : [{ key: "none", label: language === "en" ? "No critical pillar" : "Aucun pilier critique", n: null }]).map((x) => (
                      <div key={`warn-${x.key}`} style={{ display: "flex", justifyContent: "space-between", gap: 8, color: "var(--text)", fontSize: 13, marginTop: 3 }}>
                        <span>{x.label}</span>
                        {x.n != null ? <span style={{ color: C.red, fontWeight: 700, fontFamily: "ui-monospace,monospace" }}>{Math.round(x.n)}%</span> : null}
                      </div>
                    ))}
                  </div>
                </div>
                <SummaryBox>{`Synthèse : ${data?.fundamentalTiles?.summary || data?.fundamental || (language === "en" ? "Balanced fundamental profile with strengths and watch points." : "Profil fondamental équilibré avec des points forts et des zones de vigilance.")}`}</SummaryBox>
                <CTA label={t("seeAnalysis")} onClick={() => openAnalysis("fundamentals", data?.fundamentalNarrative || data?.fundamental)} />
              </div>
            </Card>

            <Card>
              <CardHead title={t("valuation")} right={<StatusBadge tone={valuationState.tone}>{valuationState.label}</StatusBadge>} />
              <div className="section-pad">
                {hasGauge ? (
                  <GaugeSemi
                    ratio={valuationState.ratio}
                    topLabel={fmtCur(data?.valuation?.dcfValue, displayCurrency)}
                    bottomLabel={`${language === "en" ? "Margin of safety" : "Marge de sécurité"} : ${fmtPct(data?.valuation?.mosPct)}`}
                    axisLabel={t("gaugeAxisFair")}
                    cornerLeft={t("gaugeCornerLow")}
                    cornerRight={t("gaugeCornerHigh")}
                  />
                ) : null}
                <div className="val-three">
                  <div className="val-mini">
                    <div className="val-mini-k">P/E</div>
                    <div className="val-mini-v" style={{ color: toneFor(-Math.abs(data?.valuation?.peRatio || 0)) }}>{fmtX(data?.valuation?.peRatio)}</div>
                    <div className="val-mini-s">{data?.valuation?.avgPe5y != null ? `Hist. ${fmtX(data?.valuation?.avgPe5y)}` : t("valuationHistNA")}</div>
                  </div>
                  <div className="val-mini">
                    <div className="val-mini-k">P/B</div>
                    <div className="val-mini-v" style={{ color: toneFor(-Math.abs(data?.valuation?.pbRatio || 0)) }}>{fmtX(data?.valuation?.pbRatio)}</div>
                    <div className="val-mini-s">{data?.valuation?.avgPb5y != null ? `Hist. ${fmtX(data?.valuation?.avgPb5y)}` : t("valuationHistNA")}</div>
                  </div>
                  <div className="val-mini">
                    <div className="val-mini-k">EV/EBITDA</div>
                    <div className="val-mini-v" style={{ color: toneFor(-Math.abs(data?.valuation?.evEbitda || 0)) }}>{fmtX(data?.valuation?.evEbitda)}</div>
                    <div className="val-mini-s">{data?.valuation?.avgEvEbitda5y != null ? `Hist. ${fmtX(data?.valuation?.avgEvEbitda5y)}` : t("valuationHistNA")}</div>
                  </div>
                </div>
                <div className="mini-grid-2">
                  <MiniTile label="Valeur DCF" value={fmtCur(data?.valuation?.dcfValue, displayCurrency)} color={C.amber} />
                  <MiniTile label="Prix de marché" value={fmtCur(data?.valuation?.currentPrice || data?.price, displayCurrency)} />
                  <MiniTile label="Marge de sécu." value={fmtPct(data?.valuation?.mosPct)} color={toneFor(data?.valuation?.mosPct)} />
                  <MiniTile label="Potentiel / Risque" value={fmtPct(upsidePct)} color={toneFor(upsidePct)} />
                  <MiniTile label="FCF Yield TTM" value={fmtPct((Number(data?.valuation?.fcfYieldPct) || 0) * (Math.abs(Number(data?.valuation?.fcfYieldPct || 0)) <= 1 ? 100 : 1))} color={toneFor(data?.valuation?.fcfYieldPct)} />
                  <MiniTile label="P/FCF TTM" value={fmtX(data?.valuation?.pcf)} color={toneFor(-Math.abs(data?.valuation?.pcf || 0))} />
                </div>
                <SummaryBox>{`Synthèse : ${data?.valuation?.valuationMessage || "Valorisation à mettre en perspective avec la qualité et la dynamique des résultats."}`}</SummaryBox>
                <CTA label={t("seeAnalysis")} onClick={() => openAnalysis("valuation", data?.valuation?.valuationMessage)} />
              </div>
            </Card>

            <Card>
              <CardHead title={t("marketStructure")} subtitle={c.industry || c.sector || ""} right={<StatusBadge tone={marketUi.concTone}>{marketUi.conc}</StatusBadge>} />
              <div className="section-pad">
                <div style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--muted)", fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", fontFamily: "ui-monospace,monospace" }}>
                  IHH <TipMark title={t("msIhhTip")} />
                </div>
                <div className="ms-wrap">
                  {hasHHI ? <HHIBar value={marketUi.hhi} /> : null}
                  <div className="ms-metrics">
                    <div className="ms-row">
                      <div className="ms-k">Part du Top 3 <TipMark title={t("msTop3Tip")} /></div>
                      <div className="ms-v">{marketUi.top3Label}<span className="ms-sub">{language === "en" ? "Open competition" : "Concurrence ouverte"}</span></div>
                    </div>
                    <div className="ms-row">
                      <div className="ms-k">Tendance IHH (1 an) <TipMark title={t("msHhiTrendTip")} /></div>
                      <div className="ms-v" style={{ color: Number.isFinite(marketUi.hhi1y) ? toneDelta(marketUi.hhi1y) : "var(--text-strong)" }}>
                        {marketUi.hhiArrow ? <span className="ms-arrow">{marketUi.hhiArrow}</span> : null}
                        {marketUi.hhiTrendLabel}
                        <span className="ms-sub">{marketUi.hhi1y >= 0 ? (language === "en" ? "Light consolidation" : "Légère consolidation") : (language === "en" ? "Fragmentation" : "Fragmentation")}</span>
                      </div>
                    </div>
                    <div className="ms-row">
                      <div className="ms-k">ROIC sectoriel (Δ3Y) <TipMark title={t("msRoicTip")} /></div>
                      <div className="ms-v" style={{ color: Number.isFinite(marketUi.roic3y) ? toneDelta(marketUi.roic3y) : "var(--text-strong)" }}>
                        {marketUi.roicArrow ? <span className="ms-arrow">{marketUi.roicArrow}</span> : null}
                        {marketUi.roicLabel}
                        <span className="ms-sub">{`ROIC : ${marketUi.roicWeightedLabel}`}</span>
                      </div>
                    </div>
                    <div className="ms-row">
                      <div className="ms-k">Acteurs gagnant des parts <TipMark title={t("msWinnersTip")} /></div>
                      <div className="ms-v">{marketUi.winnersLabel}<span className="ms-sub">{language === "en" ? "Competitive market" : "Marché compétitif"}</span></div>
                    </div>
                  </div>
                </div>
                <div className="ms-phase" style={{ color: marketUi.phaseTone === "pos" ? C.green : marketUi.phaseTone === "warn" ? C.amber : C.red }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    {language === "en" ? "Phase:" : "Phase :"} {marketUi.phase} <TipMark title={t("msPhaseTip")} />
                  </span>
                </div>
                {marketUi.cluster ? <div className="ms-cluster"><span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>CLUSTER | {marketUi.cluster} <TipMark title={t("msClusterTip")} /></span></div> : null}
                <SummaryBox>{`Synthèse : ${data?.marketStructure?.narrative || "Structure concurrentielle à lire avec la tendance de concentration et les gains de parts."}`}</SummaryBox>
                <CTA label={t("seeAnalysis")} onClick={() => openAnalysis("market", data?.marketStructure?.narrative)} />
              </div>
            </Card>

            <Card>
              <CardHead title={t("timing")} subtitle={c.symbol || symbol} right={<StatusBadge tone={statusTone(data?.timing?.trendLabel)}>{data?.timing?.trendLabel || "Neutral"}</StatusBadge>} />
              <div className="section-pad">
                <div className="timing-head">
                  <div className="timing-price-wrap">
                    <span className="timing-price">{fmtHeroPrice(timingUi.lastClose, c.currency || c.exchange, 4)}</span>
                    <span className="timing-var" style={{ color: toneFor(timingUi.periodPct) }}>{fmtPct(timingUi.periodPct)}</span>
                    <span className="timing-period-label">{language === "en" ? "over period" : "sur la période"}</span>
                  </div>
                  <span className="timing-range">{timingUi.startLabel} → {timingUi.endLabel}</span>
                </div>
                {hasPriceChart ? (
                  <div className="timing-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={chartData} margin={{ top: 10, right: 52, left: 6, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="2 4" stroke="var(--grid-line)" vertical={false} />
                        <XAxis dataKey="lbl" tick={{ fill: "var(--muted)", fontSize: 10 }} tickLine={false} axisLine={false} />
                        <YAxis yAxisId="price" domain={["auto", "auto"]} tick={{ fill: "var(--muted)", fontSize: 10 }} tickLine={false} axisLine={false} width={44} />
                        <YAxis
                          yAxisId="vol"
                          orientation="right"
                          domain={[0, "auto"]}
                          tick={{ fill: "var(--muted)", fontSize: 9 }}
                          tickLine={false}
                          axisLine={false}
                          width={48}
                          tickFormatter={formatVolumeAxisTick}
                        />
                        <RechartsTooltip />
                        <Bar
                          yAxisId="vol"
                          dataKey="vol"
                          fill="rgba(181,154,91,0.22)"
                          stroke="rgba(181,154,91,0.35)"
                          strokeWidth={0.5}
                          barSize={chartData.length > 100 ? 2 : chartData.length > 50 ? 3 : 5}
                        />
                        <Area yAxisId="price" type="monotone" dataKey="close" stroke="#9aa0a8" fill="rgba(148,163,184,.06)" strokeWidth={1.4} />
                        <Line yAxisId="price" type="monotone" dataKey="sma20" stroke="#3b82f6" dot={false} strokeWidth={2} />
                        <Line yAxisId="price" type="monotone" dataKey="sma50" stroke="#b59a5b" dot={false} strokeWidth={2} />
                        <Line yAxisId="price" type="monotone" dataKey="sma200" stroke="#9ca3af" dot={false} strokeWidth={2} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                ) : null}
                <div className="timing-legend">
                  <div className="timing-legend-item">
                    <span className="timing-legend-line" style={{ background: "#3b82f6" }} /> MMA 20 <TipMark title={t("timingMma20Tip")} /> <span style={{ color: "#c6523f" }}>{fmtHeroPrice(timingUi.sma20, c.currency || c.exchange, 4)}</span>
                  </div>
                  <div className="timing-legend-item">
                    <span className="timing-legend-line" style={{ background: "#b59a5b" }} /> MMA 50 <TipMark title={t("timingMma50Tip")} /> <span style={{ color: "#c6523f" }}>{fmtHeroPrice(timingUi.sma50, c.currency || c.exchange, 4)}</span>
                  </div>
                  <div className="timing-legend-item">
                    <span className="timing-legend-line" style={{ background: "#9ca3af" }} /> MMA 200 <TipMark title={t("timingMma200Tip")} /> <span style={{ color: "#c6523f" }}>{fmtHeroPrice(timingUi.sma200, c.currency || c.exchange, 4)}</span>
                  </div>
                </div>
                <div className="mini-grid-2" style={{ marginTop: 10 }}>
                  <MiniTile label={<span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>Tendance <TipMark title={t("timingTrendTip")} /></span>} value={timingUi.trendLabel} color={timingUi.trendTone === "pos" ? C.green : timingUi.trendTone === "neg" ? C.red : C.amber} />
                  <MiniTile label={<span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>Volume <TipMark title={t("timingVolumeTip")} /></span>} value={timingUi.volumeLabel} color={timingUi.volumeTone === "pos" ? C.green : timingUi.volumeTone === "neg" ? C.orange : C.amber} />
                  <MiniTile label={<span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>Momentum <TipMark title={t("timingMomentumTip")} /></span>} value={timingUi.momentumLabel} color={timingUi.momentumTone === "pos" ? C.green : timingUi.momentumTone === "neg" ? C.red : C.amber} />
                  <MiniTile label={<span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>Structure <TipMark title={t("timingStructureTip")} /></span>} value={timingUi.structureLabel} color={timingUi.structureTone === "pos" ? C.green : timingUi.structureTone === "neg" ? C.red : C.amber} />
                </div>
                <SummaryBox>{`Synthèse : ${data?.timing?.timingNarrative || "Le timing combine tendance, momentum et positionnement prix/moyennes mobiles."}`}</SummaryBox>
                <CTA label={t("seeAnalysis")} onClick={() => openAnalysis("timing", data?.timing?.timingNarrative)} />
              </div>
            </Card>

            <Card>
              <CardHead title={t("risk")} right={<StatusBadge tone={riskUi.badgeTone}>{riskUi.badgeLabel}</StatusBadge>} />
              <div className="section-pad">
                {hasRiskPoint ? (
                  <div style={{ height: 188, marginTop: 4, marginBottom: 12 }}>
                    <RiskQuadrant cagrPct={data?.riskReturn?.cagrWeighted} drawdownPct={data?.riskReturn?.maxDrawdown} symbol={c.symbol || symbol} />
                  </div>
                ) : null}
                <div className={`mini-grid-2-risk${hasRiskPoint ? "" : " mini-grid-2-risk--no-chart"}`}>
                  <MiniTile label="Rendement annuel (CAGR)" value={fmtPct(data?.riskReturn?.cagrWeighted)} color={toneFor(data?.riskReturn?.cagrWeighted)} />
                  <MiniTile label="Drawdown max" value={fmtPct(data?.riskReturn?.maxDrawdown)} color={toneFor(data?.riskReturn?.maxDrawdown)} />
                  <MiniTile label="Volatilité" value={fmtPct(data?.riskReturn?.volatility)} color={C.amber} />
                  <MiniTile label="Sharpe" value={fmtNum(data?.riskReturn?.sharpe)} color={C.orange} />
                </div>
                <div style={{ marginTop: 14, borderRadius: 14, overflow: "hidden", border: "1px solid var(--sep)", display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                  <div style={{ padding: "12px 14px", background: `${riskUi.profileTone === "pos" ? C.green : riskUi.profileTone === "warn" ? C.amber : C.red}14` }}>
                    <div style={{ fontSize: 11, color: "var(--muted)", fontFamily: "ui-monospace,monospace", letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 700 }}>Profil risque</div>
                    <div style={{ marginTop: 4, fontSize: 17, fontWeight: 700, lineHeight: 1.15, color: riskUi.profileTone === "pos" ? C.green : riskUi.profileTone === "warn" ? C.amber : C.red }}>{riskUi.profileLabel}</div>
                  </div>
                  <div style={{ padding: "12px 14px", background: `${riskUi.perfTone === "pos" ? C.green : riskUi.perfTone === "warn" ? C.amber : C.red}12` }}>
                    <div style={{ fontSize: 11, color: "var(--muted)", fontFamily: "ui-monospace,monospace", letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 700 }}>Rendement</div>
                    <div style={{ marginTop: 4, fontSize: 17, fontWeight: 700, lineHeight: 1.15, color: riskUi.perfTone === "pos" ? C.green : riskUi.perfTone === "warn" ? C.amber : C.red }}>{riskUi.perfLabel}</div>
                  </div>
                </div>
                <SummaryBox>{`Synthèse : ${data?.riskReturn?.narrative || "Lecture rendement/risque : arbitrer potentiel et profondeur des baisses."}`}</SummaryBox>
                <CTA label={t("seeAnalysis")} onClick={() => openAnalysis("risk", data?.riskReturn?.narrative)} />
              </div>
            </Card>

            <Card>
              <CardHead
                title={t("financial")}
                subtitle={data?.capital?.coverageLabel || t("efCoverageSubtitle")}
                right={
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <span style={{ fontFamily: "ui-monospace,monospace", fontWeight: 800, fontSize: 13, color: financialUi.pctColor }}>
                      {financialUi.coveragePct != null ? `${financialUi.coveragePct.toFixed(1)}%` : "—"}
                    </span>
                    <StatusBadge tone={financialUi.badgeTone}>
                      {financialUi.badgeFromApi ||
                        (financialUi.coveragePct != null
                          ? financialUi.coveragePct >= 100
                            ? t("efAutonomous")
                            : financialUi.coveragePct >= 50
                              ? t("efPartial")
                              : t("efExternalLeverage")
                          : t("inReview"))}
                    </StatusBadge>
                  </div>
                }
              />
              <div className="section-pad ef-section">
                <div className="ef-hero">
                  <div className="ef-hero-left">
                    <span className="ef-hero-cfo-val">{formatMegaMFromDisplay(financialUi.cfoM)}</span>
                    <span className="ef-hero-cfo-unit">
                      {" "}
                      M€ CFO
                      <TipMark title={t("efCfoTip")} />
                    </span>
                  </div>
                  <div className="ef-hero-right">
                    <span className="ef-hero-slash">/</span>
                    <span className="ef-hero-needs-val">{formatMegaMFromDisplay(financialUi.needsM)}</span>
                    <span className="ef-hero-needs-lbl">
                      {" "}
                      M€ {language === "en" ? "needs" : "besoins"}
                      <TipMark title={t("efNeedsTip")} />
                    </span>
                  </div>
                </div>
                {financialUi.hasMeter ? (
                  <StackedCoverageMeter
                    segments={financialUi.segments.map((s) => ({ ...s, label: t(s.labelKey) }))}
                    pointerPct={financialUi.pointerLinearPct}
                    pointerBarColor={financialUi.pointerBarColor}
                    pointerLabelColor={financialUi.pointerLabelColor}
                    pointerLabel={
                      financialUi.coveragePct != null && Number.isFinite(financialUi.coveragePct)
                        ? `${t("efCfoPointer")} ${financialUi.coveragePct.toFixed(1)}%`
                        : `${t("efCfoPointer")} —`
                    }
                    leftLabel={t("efMeterLeft")}
                    rightLabel={t("efMeterRight")}
                  />
                ) : (
                  <div style={{ marginTop: 10, padding: "12px 14px", borderRadius: 12, border: "1px dashed var(--sep)", color: "var(--muted)", fontSize: 12 }}>
                    {language === "en" ? "Not enough breakdown to draw the coverage bar (needs components)." : "Données insuffisantes pour dessiner la barre de couverture (composantes des besoins)."}
                  </div>
                )}
                <div className="ef-sep" />
                <div className="ef-grid-2">
                  {financialUi.rows.slice(0, 2).map((row) => (
                    <EFMetricCell key={row.id} label={t(row.labelKey)} tip={row.tip} amountRaw={row.amount} mult={row.mult} status={row.status} t={t} />
                  ))}
                </div>
                <div className="ef-sep ef-sep-tight" />
                <div className="ef-grid-2">
                  {financialUi.rows.slice(2, 4).map((row) => (
                    <EFMetricCell key={row.id} label={t(row.labelKey)} tip={row.tip} amountRaw={row.amount} mult={row.mult} status={row.status} t={t} />
                  ))}
                </div>
                <div className="ef-sep" />
                <SummaryBox>{`Synthèse : ${data?.capital?.narrative || "La structure de financement doit rester cohérente avec la génération de cash."}`}</SummaryBox>
                <CTA label={t("seeAnalysis")} onClick={() => openAnalysis("financial", data?.capital?.narrative)} />
              </div>
            </Card>

            <Card>
              <CardHead
                title={t("backtest")}
                subtitle={`vs ${backtestMetaResolved?.benchmark || "index"}`}
                right={<StatusBadge>{backtestMetaResolved?.periodLabel || t("period")}</StatusBadge>}
              />
              <div className="section-pad">
                <div className="mini-grid-2">
                  <MiniTile label={<span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>Montant initial <TipMark title={t("btTipInitial")} /></span>} value={fmtCur(backtestMetaResolved?.initialAmount, displayCurrency)} />
                  <MiniTile label={<span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>Dividendes reçus <TipMark title={t("btTipDividends")} /></span>} value={fmtCur(backtestMetaResolved?.dividendsReceived, displayCurrency)} color={C.green} />
                  <MiniTile label={<span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>Gain / Perte <TipMark title={t("btTipGain")} /></span>} value={fmtCur(backtestMetaResolved?.gainAmount, displayCurrency)} color={toneFor(backtestMetaResolved?.gainAmount)} />
                  <MiniTile
                    label={<span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>Montant final TR <TipMark title={t("btTipFinal")} /></span>}
                    value={fmtCur(backtestMetaResolved?.finalAmount, displayCurrency)}
                    color={C.green}
                    sub={
                      backtestMetaResolved?.finalMultiple != null && backtestMetaResolved?.finalTrMultiple != null
                        ? `Prix ×${fmtNum(backtestMetaResolved.finalMultiple, 2)} · TR ×${fmtNum(backtestMetaResolved.finalTrMultiple, 2)}`
                        : undefined
                    }
                  />
                </div>
                {hasBacktestChart ? (
                  <div className="bt-chart-wrap" style={{ marginTop: 12, height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={backtestData} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="2 4" stroke="var(--grid-line)" />
                        <XAxis dataKey="x" tick={{ fill: "var(--muted)", fontSize: 11 }} tickLine={false} axisLine={false} />
                        <YAxis
                          tick={{ fill: "var(--muted)", fontSize: 10 }}
                          tickLine={false}
                          axisLine={false}
                          width={40}
                          domain={[0, "auto"]}
                          tickFormatter={(v) => `×${fmtNum(v, 2)}`}
                        />
                        <RechartsTooltip
                          formatter={(value, name) => [
                            value != null ? `×${fmtNum(Number(value), 3)}` : "—",
                            name === "trMult" ? "TR" : name === "benchMult" ? (backtestMetaResolved?.benchmark || "Benchmark") : "Prix",
                          ]}
                          labelFormatter={(l) => String(l)}
                        />
                        <Line type="monotone" dataKey="trMult" name="trMult" stroke={C.green} strokeWidth={2.5} dot={false} connectNulls />
                        <Line type="monotone" dataKey="pxMult" name="pxMult" stroke={C.amber} strokeWidth={2.2} dot={false} connectNulls />
                        <Line type="monotone" dataKey="benchMult" name="benchMult" stroke="#9ca3af" strokeWidth={2} dot={false} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                    <div className="bt-legend" style={{ display: "flex", flexWrap: "wrap", gap: "12px 16px", marginTop: 8, justifyContent: "center" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span style={{ width: 18, height: 3, borderRadius: 2, background: C.green }} />
                        {language === "en" ? "Total return (TR)" : "TR (total return)"}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span style={{ width: 18, height: 3, borderRadius: 2, background: C.amber }} />
                        {language === "en" ? "Price" : "Prix"}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span style={{ width: 18, height: 3, borderRadius: 2, background: "#9ca3af" }} />
                        {backtestMetaResolved?.benchmark || "Benchmark"}
                      </span>
                    </div>
                  </div>
                ) : null}
                <div className="mini-grid-3" style={{ marginTop: 30 }}>
                  <MiniTile label="CAGR PRIX" value={fmtPct(backtestMetaResolved?.cagrPrice)} color={C.amber} />
                  <MiniTile label="CAGR TR" value={fmtPct(backtestMetaResolved?.cagrTr)} color={cagrTrColor} />
                  <MiniTile label="CAGR indice" value={fmtPct(backtestMetaResolved?.cagrBenchmark)} />
                  <MiniTile label="xPRIX" value={fmtX(backtestMetaResolved?.xPrice)} color={C.amber} />
                  <MiniTile label="xTR" value={fmtX(backtestMetaResolved?.xTr)} color={C.green} />
                  <MiniTile label="Max drawdown" value={fmtPct(backtestMetaResolved?.maxDrawdown)} color={C.red} />
                </div>
                <SummaryBox>{`Synthèse : ${backtestMetaResolved?.summary || "Le backtest situe la performance du titre face à son indice de référence."}`}</SummaryBox>
                <CTA label={t("seeAnalysis")} onClick={() => openAnalysis("backtest", backtestMetaResolved?.summary)} />
              </div>
            </Card>

            <Card>
              <CardHead
                title={t("distribution")}
                subtitle={t("dividendsCalendar")}
                right={
                  <StatusBadge tone={distributionUi.scoreTone}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      {distributionUi.qualityLabel} · {distributionUi.scoreRounded}/100
                      <TipMark title={t("distTipScore")} />
                    </span>
                  </StatusBadge>
                }
              />
              <div className="section-pad dist-section">
                <div className="dist-hero-row">
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
                    <span className="dist-hero-amt" style={{ color: C.green, fontFamily: "ui-monospace,monospace", fontWeight: 800, lineHeight: 1 }}>
                      {distributionUi.hasAnnual ? fmtNum(distributionUi.annual, 2) : "—"}
                    </span>
                    <span className="dist-hero-unit" style={{ color: "var(--muted)", fontWeight: 600 }}>
                      {`${distributionUi.currency || displayCurrency} / ${language === "en" ? "year" : "an"}`}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        borderRadius: 10,
                        padding: "6px 10px",
                        background: `${C.amber}14`,
                        border: `1px solid ${C.amber}30`,
                      }}
                    >
                      <span style={{ fontFamily: "ui-monospace,monospace", fontSize: "clamp(9px, 2.4vw, 11px)", fontWeight: 700, color: C.amber }}>
                        {distributionUi.inferredFreq}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        borderRadius: 10,
                        padding: "6px 10px",
                        background: `${C.green}12`,
                        border: `1px solid ${C.green}28`,
                      }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: 999, background: C.green }} />
                      <span style={{ fontFamily: "ui-monospace,monospace", fontSize: "clamp(9px, 2.4vw, 11px)", fontWeight: 700, color: C.green }}>
                        {distributionUi.paidCount} {t("distMonthsPaid")}
                      </span>
                    </div>
                  </div>
                </div>

                {distributionUi.hasAnnual || distributionUi.paidCount > 0 ? (
                  <>
                    <div className="dist-cal-grid">
                      {months.map((m, idx) => (
                        <DistributionMonthPill
                          key={m}
                          label={m}
                          paid={distributionUi.byMonth[idx].count > 0}
                          total={distributionUi.byMonth[idx].total}
                          currency={distributionUi.currency}
                          payment={distributionUi.pmtByMonth[idx]}
                        />
                      ))}
                    </div>
                    <div className="dist-legend-row" style={{ display: "flex", flexWrap: "wrap", gap: "12px 20px", marginBottom: 10, paddingTop: 2 }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10, fontWeight: 700, color: "var(--muted)" }}>
                        <span style={{ width: 10, height: 10, borderRadius: 999, background: C.green }} />
                        {t("distLegendPaid")}
                      </span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10, fontWeight: 700, color: "var(--muted)" }}>
                        <span style={{ width: 10, height: 10, borderRadius: 999, background: "var(--sep)" }} />
                        {t("distLegendNone")}
                      </span>
                    </div>
                    <div className="dist-bar-wrap">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={distributionUi.barData} margin={{ top: 8, right: 4, left: 0, bottom: 2 }} barCategoryGap="18%">
                          <CartesianGrid strokeDasharray="2 4" stroke="var(--grid-line)" vertical={false} />
                          <XAxis dataKey="label" tick={{ fontSize: 9, fill: "var(--muted)" }} tickLine={false} axisLine={false} interval={0} height={28} />
                          <YAxis hide domain={[0, distributionUi.maxBar * 1.08]} />
                          {distributionUi.peakPaid && Number(distributionUi.barData[distributionUi.peakIdx]?.amt) > 0 ? (
                            <ReferenceLine
                              x={distributionUi.barData[distributionUi.peakIdx]?.label}
                              stroke={C.green}
                              strokeDasharray="4 4"
                              strokeOpacity={0.45}
                            />
                          ) : null}
                          <RechartsTooltip
                            formatter={(v) => [v != null && Number(v) > 0 ? `${fmtNum(Number(v), 2)} EUR` : "—", language === "en" ? "Dividend" : "Dividende"]}
                            labelStyle={{ color: "var(--text-strong)", fontWeight: 700 }}
                            contentStyle={{ borderRadius: 10, border: "1px solid var(--sep)", fontSize: 11 }}
                          />
                          <Bar dataKey="amt" radius={[5, 5, 0, 0]} maxBarSize={40}>
                            {distributionUi.barData.map((entry, index) => (
                              <Cell
                                key={`dist-bar-${index}`}
                                fill={
                                  entry.paid
                                    ? lerpColor(shade(C.green, 0.2), C.green, 0.55 + Math.min(0.35, (Number(entry.amt) / distributionUi.maxBar) * 0.35))
                                    : "rgba(30,40,34,.07)"
                                }
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                ) : null}

                <div className="dist-metrics-grid">
                  <DistMetricTile
                    label={language === "en" ? "Yield" : "Yield"}
                    value={distributionUi.yieldDisplay}
                    color={distributionUi.yieldCol}
                    tip={t("distTipYield")}
                  />
                  <DistMetricTile label={language === "en" ? "Frequency" : "Fréquence"} value={distributionUi.inferredFreq} color={C.green} tip={t("distTipFreq")} />
                  <DistMetricTile label="Payout ratio" value={distributionUi.payoutDisplay} color={distributionUi.payoutCol} tip={t("distTipPayout")} />
                  <DistMetricTile
                    label={language === "en" ? "FCF cover" : "Couverture FCF"}
                    value={distributionUi.fcfDisplay}
                    color={distributionUi.fcfCol}
                    tip={t("distTipFcf")}
                  />
                  <DistMetricTile label={language === "en" ? "Growth 5Y" : "Croissance 5Y"} value={distributionUi.growthDisplay} color={distributionUi.growthCol} tip={t("distTipGrowth")} />
                  <DistMetricTile label={language === "en" ? "Regularity" : "Régularité"} value={distributionUi.regularityStr} color={distributionUi.regCol} tip={t("distTipRegularity")} />
                </div>

                <SummaryBox>
                  <span style={{ fontWeight: 700, color: "var(--text-strong)" }}>{language === "en" ? "Summary:" : "Synthèse :"}</span>{" "}
                  <span style={{ color: "var(--text)" }}>{distributionUi.summaryText}</span>
                </SummaryBox>
                <CTA label={t("seeAnalysis")} onClick={() => openAnalysis("distribution", distributionUi.summaryText)} />
              </div>
            </Card>

          </div>
        ) : null}

        {activeAnalysis ? (
          <div
            className="analysis-modal-overlay"
            onClick={() => setActiveAnalysis(null)}
          >
            <div className="analysis-modal" onClick={(e) => e.stopPropagation()}>
              <CardHead
                title={t("detailed")}
                right={
                  <button
                    className="analysis-modal-close-x"
                    onClick={() => setActiveAnalysis(null)}
                    aria-label={t("close")}
                    title={t("close")}
                  >
                    ×
                  </button>
                }
              />
              <div className="analysis-modal-body">
                <SummaryBox>{activeAnalysis.narrative || t("noData")}</SummaryBox>
                {activeAnalysis.formula ? (
                  <div style={{ marginTop: 10, borderRadius: 10, border: "1px dashed var(--sep)", padding: "10px 12px", color: "var(--muted)", fontSize: 11, lineHeight: 1.45, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                    <strong style={{ color: "var(--text-strong)", fontFamily: "inherit" }}>{t("calcSource")}:</strong>{" "}
                    {activeAnalysis.formula}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {activeFundTip ? (
          <div className="fund-tip-drawer-overlay" onClick={() => setActiveFundTip(null)}>
            <div className="fund-tip-drawer" onClick={(e) => e.stopPropagation()}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div className="fund-tip-drawer-title">{activeFundTip.label}</div>
                <button className="analysis-modal-close" onClick={() => setActiveFundTip(null)}>
                  {t("close")}
                </button>
              </div>
              <div className="fund-tip-drawer-body">{activeFundTip.text}</div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default TickerAnalysis;
